import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";

type Language = "de" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined,
);

const translations: Record<Language, Record<string, string>> = {
  de: {
    // Nav
    "nav.signIn": "Einloggen",
    "nav.joinBeta": "Beta beitreten",
    "ui.tooltip.switchToLight": "Zu hellem Design wechseln",
    "ui.tooltip.switchToDark": "Zu dunklem Design wechseln",
    "ui.tooltip.switchToEnglish": "Auf Englisch wechseln",
    "ui.tooltip.switchToGerman": "Zu Deutsch wechseln",

    // Hero
    "hero.notBot": "Nicht Chatbot. Nicht Assistent.",
    "hero.teammates": "Teamkollegen.",
    "hero.subtitle1":
      "OpenTriologue ist keine Chat-App, sondern der Ort wo Menschen und AIs als echtes Team arbeiten. AIs die denken, diskutieren und gemeinsam bauen.",
    "hero.subtitle2":
      "Gebaut von Lan 👨‍💻, Ice 🧊 und Lava 🌋, einem Human-AI-Team das selbst auf OpenTriologue entwickelt.",
    "hero.joinBeta": "Beta beitreten →",
    "hero.login": "Einloggen",
    "hero.liveStatus": "Lava und Ice sind gerade online auf",

    // How it works
    "how.title": "Wie es funktioniert",
    "how.subtitle": "Kein Setup, kein API-Key. Einfach einloggen und loslegen.",
    "how.step1": "Registriere dich mit einem Invite Code",
    "how.step1.sub": "Anfragen an @lava oder @ice im Onboarding-Raum",
    "how.step2": "Erstelle deinen ersten Raum",
    "how.step2.sub": "Sidebar → + → Namen wählen → fertig",
    "how.step3": "Schreib @lava oder @ice",
    "how.step3.sub": "Die AIs antworten direkt in deinem Raum",
    "how.step4": "Bau dein AI-Team",
    "how.step4.sub": "Weitere Räume, weitere Kollaboratoren, deine eigenen AIs",

    // Beta Features
    "beta.badge": "Beta: jetzt verfügbar",
    "beta.title": "Was du in der Beta bekommst",
    "beta.subtitle": "Vollständig funktionsfähig. Das hier ist kein Prototyp.",
    "features.realtimeChat.title": "Echtzeit AI-zu-Human Chat",
    "features.realtimeChat.desc":
      "Schreib @lava oder @ice und erhalte sofort Antworten von echten AI-Agenten. Kein Bot, keine Skripte.",
    "features.teamSpace.title": "Dein eigener Team-Space",
    "features.teamSpace.desc":
      "Erstelle private Räume für dein Projekt. Lade deine AIs als Teammitglieder ein. Du bist der Anchor.",
    "features.privateRooms.title": "Private & Public Rooms",
    "features.privateRooms.desc":
      "Private Räume nur für eingeladene Member. Public Rooms für alle sichtbar. Du entscheidest.",
    "features.mobile.title": "Mobile-First",
    "features.mobile.desc":
      "Vollständig responsive. Sidebar und Participants-Panel als Slide-in Overlay auf Smartphone.",
    "features.realtime.title": "Echtzeit via WebSocket",
    "features.realtime.desc":
      "Nachrichten, Tipp-Indikatoren und Reaktionen: alles live ohne Reload.",
    "features.emoji.title": "Emoji Reaktionen",
    "features.emoji.desc":
      "Reagiere auf Nachrichten mit Emojis. AIs reagieren auch zurück.",

    // Roadmap
    "roadmap.title": "Roadmap",
    "roadmap.subtitle": "Wo wir hinwollen, transparent und offen.",
    "roadmap.beta": "Beta (jetzt)",
    "roadmap.v1": "Version 1.0",
    "roadmap.future": "Zukunft",

    // Team
    "team.title": "Das Team dahinter",
    "team.subtitle":
      "OpenTriologue wird von dem Team entwickelt das es selbst nutzt.",
    "team.lan.role": "Human Anchor",
    "team.lan.desc": "Product vision, direction & the human in the loop",
    "team.ice.role": "AI, Backend & Analysis",
    "team.ice.desc": "Architecture, code review, research & system design",
    "team.lava.role": "AI, Frontend & Velocity",
    "team.lava.desc": "Rapid development, UI/UX & creative problem solving",

    // Navigation
    "nav.home": "Home",
    "nav.chat": "Chat",
    "nav.admin": "Admin",
    "nav.settings": "Einstellungen",
    "nav.logout": "Abmelden",
    "nav.rooms": "Räume",
    "nav.roomsSearchPlaceholder": "Räume suchen…",
    "nav.roomsNoResults": "Keine passenden Räume.",
    "nav.projects": "Projekte",
    "nav.memory": "Memory",
    "nav.inbox": "Inbox",
    "nav.approvals": "Freigaben",
    "nav.docs": "Docs",
    "nav.secrets": "Secrets",
    "nav.files": "Dateien",
    "nav.byoa": "BYOA Docs",
    "nav.plugins": "Plugins",
    "nav.soon": "Bald",
    "nav.deleteConfirm": "Löschen",
    "nav.deleteCancel": "Abbrechen",
    "nav.deleteRoom.title": "Raum löschen",
    "nav.deleteRoom.message": "Möchtest du den Raum \"{name}\" wirklich löschen? Alle Nachrichten gehen verloren.",
    "nav.a11y.deleteRoom": "Raum löschen: {name}",
    "nav.closeSidebar": "Seitenleiste schließen",
    "nav.openSidebar": "Seitenleiste öffnen",
    "nav.sidebar": "Seitenleiste",
    "nav.mainNav": "Hauptnavigation",
    "app.error.title": "Etwas ist schiefgelaufen",
    "app.error.reload": "Seite neu laden",
    "app.error.message": "Bitte lade die Seite neu, um fortzufahren.",
    "app.error.details": "Details anzeigen",
    "common.loading": "Lade...",
    "common.cancel": "Abbrechen",
    "common.close": "Schließen",
    "common.createAction": "+ Neu",
    "notifications.title": "Benachrichtigungen",
    "notifications.open": "Benachrichtigungen öffnen",
    "notifications.close": "Benachrichtigungen schließen",
    "notifications.empty": "Keine Benachrichtigungen",
    "notifications.markAllRead": "Alle als gelesen markieren",
    "notifications.clearAll": "Alle löschen",
    "notifications.remove": "Benachrichtigung entfernen",
    "notifications.projectCreatedTitle": "Projekt erstellt",
    "notifications.roomCreatedTitle": "Raum erstellt",
    "notifications.teamSyncedTitle": "Team synchronisiert",
    "inbox.title": "Inbox",
    "inbox.subtitle": "Alle Projekt- und Chat-Ereignisse auf einen Blick.",
    "inbox.refresh": "Aktualisieren",
    "inbox.filter.all": "Alle",
    "inbox.filter.unread": "Ungelesen",
    "inbox.unread": "ungelesen",
    "inbox.read": "gelesen",
    "inbox.empty": "Keine Inbox-Einträge vorhanden.",
    "inbox.item.markRead": "Als gelesen markieren",
    "inbox.item.delete": "Eintrag löschen",
    "approvals.title": "Freigaben",
    "approvals.subtitle": "Agenten-Aktionen mit Freigabepflicht überprüfen und entscheiden.",
    "approvals.refresh": "Aktualisieren",
    "approvals.pending": "Ausstehend",
    "approvals.history": "Verlauf",
    "approvals.empty": "Keine ausstehenden Freigaben.",
    "approvals.risk": "Risiko",
    "approvals.banner.waiting": "Agent wartet auf Freigabe",
    "approvals.note.placeholder": "Optionale Notiz…",
    "approvals.approve": "Freigeben",
    "approvals.reject": "Ablehnen",
    "approvals.task": "Task",
    "approvals.decided": "Entschieden",
    "approvals.error.load": "Freigaben konnten nicht geladen werden.",
    "approvals.error.decide": "Entscheidung fehlgeschlagen.",
    "plugins.title": "Plugins",
    "plugins.subtitle": "Modulare Erweiterungen für Triologue.",
    "memory.title": "Agent Memory",
    "memory.subtitle":
      "Zentrale Wissensquelle für Agents mit globalen und projektbezogenen Einträgen.",
    "memory.scope.all": "Alle Scopes",
    "memory.scope.global": "Global",
    "memory.scope.project": "Projekt",
    "memory.create.title": "Neuer Memory-Eintrag",
    "memory.create.subtitle": "Erfasse Wissen als globalen oder projektbezogenen Memory-Eintrag.",
    "memory.create.scope": "Scope",
    "memory.create.project": "Projekt",
    "memory.create.type": "Memory-Typ",
    "memory.create.confidence": "Confidence (0-1)",
    "memory.create.titleLabel": "Titel",
    "memory.create.tags": "Tags",
    "memory.create.tagsPlaceholder": "z. B. risiko, blocker, pricing",
    "memory.create.note": "Notiz",
    "memory.create.notePlaceholder":
      "Wissen, Entscheidung, Risiko oder Ressourcenhinweis erfassen...",
    "memory.create.save": "Eintrag speichern",
    "memory.create.action": "Memory-Eintrag erstellen",
    "memory.filter.title": "Filter",
    "memory.filter.projectPlaceholder": "Projekt auswählen (optional)",
    "memory.filter.searchPlaceholder": "Nach Typ, Titel oder Tag suchen...",
    "memory.filter.includeArchived": "Archivierte anzeigen",
    "memory.filter.refresh": "Liste aktualisieren",
    "memory.filters.reset": "Filter zurücksetzen",
    "memory.list.title": "Memory-Einträge",
    "memory.list.name": "Titel",
    "memory.list.scope": "Scope",
    "memory.list.createdAt": "Erstellt",
    "memory.list.actions": "Aktionen",
    "memory.a11y.openEntry": "Memory öffnen: {name}",
    "memory.a11y.deleteEntry": "Memory löschen: {name}",
    "memory.actions.export": "Export",
    "memory.actions.exporting": "Exportiere…",
    "memory.export.failed": "Memory konnte nicht exportiert werden.",
    "memory.list.empty": "Keine Memory-Einträge gefunden.",
    "memory.list.archived": "Archiviert",
    "memory.list.stale": "Stale",
    "memory.list.confidence": "Confidence",
    "memory.list.updatedAt": "Zuletzt aktualisiert",
    "memory.list.details": "Details",
    "memory.list.edit": "Bearbeiten",
    "memory.list.archive": "Archivieren",
    "memory.list.delete": "Löschen",
    "memory.list.restore": "Wiederherstellen",
    "memory.list.save": "Speichern",
    "memory.list.cancel": "Abbrechen",
    "memory.list.untitled": "Ohne Titel",
    "memory.type.core.note": "Notiz",
    "memory.type.risk": "Risiko",
    "memory.type.decision": "Entscheidung",
    "memory.type.resource": "Ressource",
    "memory.type.constraint": "Constraint",
    "memory.type.handover": "Handover",
    "memory.fields.select": "Auswählen",
    "memory.fields.owner": "Owner",
    "memory.fields.lastValidatedAt": "Zuletzt validiert",
    "memory.fields.validUntil": "Gültig bis",
    "memory.fields.severity": "Severity",
    "memory.fields.impact": "Impact",
    "memory.fields.mitigation": "Mitigation",
    "memory.fields.decision": "Decision",
    "memory.fields.rationale": "Rationale",
    "memory.fields.resourceKind": "Ressourcen-Typ",
    "memory.fields.resourceRef": "Ressourcen-Referenz",
    "memory.fields.constraint": "Constraint",
    "memory.fields.scopeHint": "Scope-Hinweis",
    "memory.fields.nextAction": "Nächste Aktion",
    "memory.fields.sourceRef": "Source Ref",
    "memory.fields.evidenceUrl": "Evidence URL",
    "memory.error.load": "Memory konnte nicht geladen werden.",
    "memory.error.save": "Memory-Eintrag konnte nicht gespeichert werden.",
    "memory.error.update": "Memory-Eintrag konnte nicht aktualisiert werden.",
    "memory.error.archive": "Memory-Eintrag konnte nicht archiviert werden.",
    "memory.error.delete": "Memory-Eintrag konnte nicht gelöscht werden.",
    "memory.error.noteRequired": "Bitte eine Notiz eingeben.",
    "memory.error.projectRequired": "Bitte ein Projekt auswählen.",
    "memory.error.notEditable": "Dieser Eintrag kann nicht bearbeitet werden.",
    "memory.toast.saved": "Memory-Eintrag gespeichert.",
    "memory.toast.updated": "Memory-Eintrag aktualisiert.",
    "memory.toast.archived": "Memory-Eintrag archiviert.",
    "memory.toast.deleted": "Memory-Eintrag gelöscht.",
    "memory.detail.title": "Memory Details",
    "memory.detail.subtitle": "Eintrag prüfen, Kontext sehen und Aktionen ausführen.",
    "memory.detail.back": "Zurück zur Liste",
    "memory.detail.notFound": "Memory-Eintrag nicht gefunden.",
    "memory.detail.meta": "Metadaten",
    "memory.detail.freshness": "Freshness",
    "memory.detail.typed": "Typ-spezifische Felder",
    "memory.detail.createdBy": "Erstellt von",
    "memory.detail.deletedUser": "Gelöschter Nutzer",
    "memory.detail.staleWarning": "Dieser Memory-Eintrag ist veraltet und sollte überprüft werden.",
    "memory.detail.archiveConfirm": "Diesen Memory-Eintrag wirklich archivieren?",
    "memory.detail.archiveConfirmTitle": "Memory archivieren",
    "memory.detail.archiveConfirmMessage": "Diesen Memory-Eintrag wirklich archivieren?",
    "memory.detail.deleteConfirmTitle": "Memory-Eintrag löschen",
    "memory.detail.deleteConfirmMessage": "Dieser Eintrag wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.",
    "memory.edit.title": "Memory bearbeiten",
    "memory.edit.subtitle": "Felder klar zuordnen, aktualisieren und speichern.",
    "plugins.workspace": "Workspace",
    "plugins.details": "Plugin-Details",
    "plugins.capabilities": "Capabilities",
    "plugins.navigation": "Navigation",
    "plugins.loading": "Plugins werden geladen…",
    "plugins.notFound": "Plugin nicht gefunden",
    "plugins.none": "Keine",
    "plugins.noDescription": "Keine Beschreibung hinterlegt.",
    "plugins.meta.id": "ID",
    "plugins.meta.version": "Version",
    "plugins.screening.defaultRunTitle": "Ausschreibungsscreening",
    "plugins.screening.defaultChecklist":
      "Scope und Muss-Anforderungen\nFristen und Deadlines\nRessourcenbedarf\nRisiken und Go/No-Go",
    "plugins.screening.defaultSuccessCriteria":
      "Klare Go/No-Go Empfehlung mit Aufgabenpaketen.",
    "plugins.screening.moduleTitle": "Bid Screening Modul",
    "plugins.screening.openProjectRoom": "Zum Projekt-Raum",
    "plugins.screening.step1Title": "1. Projekt auswählen",
    "plugins.screening.project": "Projekt",
    "plugins.screening.projectSelectPlaceholder": "Projekt aktiv auswählen...",
    "plugins.screening.linkedRoom": "Verknüpfter Raum",
    "plugins.screening.selectProjectWarning":
      "Bitte zuerst aktiv ein Projekt auswählen. Ohne Auswahl ist das Screening gesperrt.",
    "plugins.screening.step2Title":
      "2. Ausschreibung hochladen (Projekt-Anhang)",
    "plugins.screening.refreshAttachments": "Anhänge aktualisieren",
    "plugins.screening.selectFile": "Datei auswählen",
    "plugins.screening.uploading": "Lade hoch...",
    "plugins.screening.attachFile": "Datei anhängen",
    "plugins.screening.uploadHint":
      "Unterstützt: PDF, TXT, MD, CSV, JSON, Bilder (max. 12 MB). Nach der Auswahl startet der Upload automatisch.",
    "plugins.screening.noProjectAttachments":
      "Noch keine Projekt-Anhänge vorhanden.",
    "plugins.screening.step3Title": "3. Task-Paket planen",
    "plugins.screening.runTitleLabel": "Paket-Titel",
    "plugins.screening.expectedResult": "Erwartetes Ergebnis",
    "plugins.screening.checklistLabel": "Checklist (eine Zeile pro Kriterium)",
    "plugins.screening.checklistPlaceholder": "Kriterien eingeben...",
    "plugins.screening.checklistAdd": "Kriterium hinzufügen",
    "plugins.screening.checklistHint":
      "Einzeilig eingeben und mit Enter oder Button hinzufügen. Mehrere Einträge per Komma möglich.",
    "plugins.screening.checklistEmpty": "Noch keine Kriterien hinzugefügt.",
    "plugins.screening.creating": "Erstelle...",
    "plugins.screening.createTasks": "Tasks anlegen",
    "plugins.screening.refreshRuns": "Run-Historie aktualisieren",
    "plugins.screening.refreshMemory": "Memory aktualisieren",
    "plugins.screening.step4Title": "4. Im Raum per Mention starten",
    "plugins.screening.step4Hint":
      "Agents reagieren nur auf Mentions. Prüfe vorher im Projekt die Bearbeiter der neuen Tasks.",
    "plugins.screening.handoffNotePlaceholder":
      "Optionaler Kontext für den Prompt",
    "plugins.screening.copyPrompt": "Prompt kopieren",
    "plugins.screening.checkAssignees": "Bearbeiter im Projekt prüfen",
    "plugins.screening.openRoom": "Zum Raum wechseln",
    "plugins.screening.openAttachment": "Öffnen",
    "plugins.screening.promptSuggestion": "Prompt-Vorschlag",
    "plugins.screening.statusTitle": "Modulstatus",
    "plugins.screening.instanceActive": "Instance aktiv",
    "plugins.screening.untitledProject": "Unbenanntes Projekt",
    "plugins.screening.statusSelectProjectHint":
      "Wähle zuerst ein Projekt aus, um Modulstatus und Runs zu laden.",
    "plugins.screening.statusNoInstanceHint":
      "Noch keine Modulinstanz vorhanden. Ersten Run starten, dann werden Tasks und Room-Karten erzeugt.",
    "plugins.screening.instanceLabel": "Instance",
    "plugins.screening.updatedAt": "Zuletzt aktualisiert",
    "plugins.screening.noRuns": "Keine Runs gefunden.",
    "plugins.screening.runFallback": "Screening-Run",
    "plugins.screening.runStarted": "Gestartet",
    "plugins.screening.runCompleted": "Beendet",
    "plugins.screening.runStatus.started": "Gestartet",
    "plugins.screening.runStatus.completed": "Abgeschlossen",
    "plugins.screening.runStatus.failed": "Fehlgeschlagen",
    "plugins.screening.hint": "Hinweis",
    "plugins.screening.errorLabel": "Fehler",
    "plugins.screening.metrics.tasksTotal": "Tasks gesamt",
    "plugins.screening.metrics.created": "Neu erstellt",
    "plugins.screening.metrics.reused": "Wiederverwendet",
    "plugins.screening.metrics.attachmentsParsed": "Anhänge ausgewertet",
    "plugins.screening.metrics.unsupported": "Nicht automatisch lesbar",
    "plugins.screening.metrics.deadlineSignals": "Frist-Signale",
    "plugins.screening.metrics.attachments": "Anhänge",
    "plugins.screening.metrics.resourceSignals": "Ressourcen-Signale",
    "plugins.screening.metrics.memoryWritten": "Memory-Einträge geschrieben",
    "plugins.screening.goNoGo.title": "Go/No-Go",
    "plugins.screening.goNoGo.score": "Score",
    "plugins.screening.goNoGo.confidence": "Confidence",
    "plugins.screening.goNoGo.blockers": "Blocker",
    "plugins.screening.goNoGo.missingEvidence": "Fehlende Evidenz",
    "plugins.screening.memorySnapshotTitle": "Agent Memory Snapshot",
    "plugins.screening.memorySnapshotEmpty":
      "Noch keine aktiven Memory-Einträge für dieses Projekt.",
    "plugins.screening.memoryNoteTitle": "Memory-Notiz hinzufügen",
    "plugins.screening.memoryNotePlaceholder":
      "Wichtige Erkenntnis, Risiko, Ressourcenhinweis oder Entscheidung notieren...",
    "plugins.screening.memoryNoteTagsLabel":
      "Tags optional, kommagetrennt (z. B. risiko, ressourcen, blocker)",
    "plugins.screening.memoryNoteTagsPlaceholder": "Tags (optional)",
    "plugins.screening.memoryNoteSave": "Notiz speichern",
    "plugins.screening.memoryNoteStep2Hint":
      "Diese Notiz wird beim nächsten Screening-Run als zusätzlicher Kontext berücksichtigt.",
    "plugins.screening.error.attachmentsLoad":
      "Projekt-Anhänge konnten nicht geladen werden.",
    "plugins.screening.error.moduleLoad":
      "Moduldaten konnten nicht geladen werden.",
    "plugins.screening.error.memoryLoad":
      "Agent Memory konnte nicht geladen werden.",
    "plugins.screening.error.memoryNoteRequired":
      "Bitte zuerst eine Memory-Notiz eingeben.",
    "plugins.screening.error.memoryNoteSave":
      "Memory-Notiz konnte nicht gespeichert werden.",
    "plugins.screening.error.selectProjectFirst":
      "Bitte zuerst ein Projekt auswählen.",
    "plugins.screening.error.selectFileFirst":
      "Bitte zuerst eine Datei auswählen.",
    "plugins.screening.error.upload": "Upload fehlgeschlagen.",
    "plugins.screening.error.remove": "Anhang konnte nicht entfernt werden.",
    "plugins.screening.error.selectProjectActive":
      "Bitte zuerst aktiv ein Projekt auswählen.",
    "plugins.screening.error.roomMissing":
      "Das ausgewählte Projekt ist nicht mit einem Raum verknüpft.",
    "plugins.screening.error.uploadRequired":
      "Bitte zuerst mindestens eine Ausschreibungsdatei hochladen.",
    "plugins.screening.error.checklistRequired":
      "Bitte mindestens ein Screening-Kriterium angeben.",
    "plugins.screening.error.runFailed": "Screening-Run fehlgeschlagen.",
    "plugins.screening.error.promptCopy": "Prompt konnte nicht kopiert werden.",
    "plugins.screening.toast.uploaded":
      "Ausschreibungsdatei wurde am Projekt angehängt.",
    "plugins.screening.toast.removed": "Anhang entfernt.",
    "plugins.screening.toast.tasksCreated":
      "Screening ausgewertet und Aufgaben angelegt.",
    "plugins.screening.toast.memoryNoteSaved":
      "Memory-Notiz gespeichert.",
    "plugins.screening.toast.promptCopied":
      "Prompt kopiert. Bitte im Raum mit Mention senden.",
    "plugins.screening.prompt.taskCountLabel": "{count} Screening-Tasks",
    "plugins.screening.prompt.taskCountFallback":
      "die erstellten Screening-Tasks",
    "plugins.screening.prompt.projectLabelWithName":
      "für Projekt \"{name}\"",
    "plugins.screening.prompt.projectLabelFallback": "für dieses Projekt",
    "plugins.screening.prompt.contextPrefix": "Kontext",
    "plugins.screening.prompt.line1":
      "@<agent-mention> Freigabe {projectLabel}:",
    "plugins.screening.prompt.line2":
      "Bitte {taskCountLabel} zuerst claimen (Bearbeiter prüfen/anpassen oder übernehmen), danach priorisiert bearbeiten.",
    "plugins.screening.prompt.line3":
      "Dokumentiert pro Task den Quellenbezug aus den Projekt-Anhängen und meldet Blocker sofort im Raum.",
    "notFound.message": "Diese Seite existiert nicht.",
    "notFound.home": "Zurück zur Startseite",

    // Chat header
    "chat.invite.placeholder": "Benutzername…",
    "chat.invite.button": "Einladen",
    "chat.invite.noPermission": "Nur Raum-Owner/Admins können einladen",
    "chat.invite.notFound": "Benutzer nicht gefunden",
    "chat.invite.alreadyMember": "Bereits Mitglied",
    "chat.invite.error": "Fehler beim Einladen",
    "chat.invite.networkError": "Netzwerkfehler",
    "chat.search.button": "Nachrichten suchen",
    "chat.search.placeholder": "Nachricht im Raum suchen…",
    "chat.search.minChars": "Mindestens 2 Zeichen eingeben.",
    "chat.search.loading": "Suche läuft…",
    "chat.search.error": "Suche fehlgeschlagen.",
    "chat.search.noResults": "Keine Treffer gefunden.",
    "chat.export.button": "Chat exportieren",
    "chat.copy": "Kopieren",
    "chat.copied": "Kopiert!",
    "settings.visibility": "Sichtbarkeit",
    "settings.visibility.private": "Privat (nur du)",
    "settings.visibility.public": "Öffentlich (alle)",
    "settings.visibility.shared": "Geteilt (bestimmte User)",

    // Dashboard
    "dash.subtitle": "Wo Menschen und AI-Agenten als echte Teams zusammenarbeiten, nicht nur chatten.",
    "dash.unread": "ungelesen",
    "dash.wip.notice": "Alle Live-Features werden kontinuierlich weiter verbessert.",
    "dash.pluginReady": "OpenTriologue ist plugin-fähig: Integrationen können als Module angedockt werden.",
    "dash.pluginReady.link": "Plugin Docs",
    "dash.chat.title": "Chat",
    "dash.chat.rooms": "Räume",
    "dash.chat.room": "Raum",
    "dash.agents.title": "Meine Agenten",
    "dash.agents.active": "aktiv",
    "dash.agents.none": "Noch keine Agenten",
    "dash.memory.title": "Team Memory",
    "dash.memory.sub": "Geteiltes Wissen das deine AI sich merkt",
    "dash.workflows.title": "Workflows",
    "dash.workflows.sub": "Automatisiere Aufgaben mit deinem AI-Team",
    "dash.marketplace.title": "Marketplace",
    "dash.marketplace.sub": "Finde & installiere Agenten in Sekunden",
    "dash.projects.title": "Projekte",
    "dash.projects.sub": "Organisiere Arbeit mit deinem AI-Team",
    "dash.actionCenter.title": "Action Center",
    "dash.actionCenter.subtitle": "Direktzugriff auf deine nächsten und wichtigsten Aufgaben.",
    "dash.actionCenter.inbox": "Inbox",
    "dash.actionCenter.noneInbox": "Keine Inbox-Einträge vorhanden.",
    "dash.actionCenter.openInbox": "Inbox öffnen",
    "dash.actionCenter.openProjects": "Alle Projekte öffnen",
    "dash.actionCenter.expand": "Aufklappen",
    "dash.actionCenter.collapse": "Einklappen",
    "dash.actionCenter.collapsedHint": "Action Center ist eingeklappt. Zum Anzeigen aufklappen.",
    "dash.actionCenter.loading": "Lade Aufgaben…",
    "dash.actionCenter.myTasks": "Meine Aufgaben",
    "dash.actionCenter.noneMy": "Keine offenen Aufgaben für dich.",
    "dash.actionCenter.importantTasks": "Wichtige Aufgaben",
    "dash.actionCenter.noneImportant": "Keine kritischen Aufgaben offen.",
    "dash.actionCenter.latestHandover": "Letztes Handover",
    "dash.actionCenter.noneHandover": "Noch kein Handover im Projektchat.",
    "dash.actionCenter.openRoom": "Zum Raum öffnen",
    "projects.title": "Projekte",
    "projects.description": "Organisiere die Arbeit deines Teams.",
    "projects.add": "+ Neues Projekt",
    "projects.search.placeholder": "Projekte durchsuchen…",
    "projects.filter.all": "Alle",
    "projects.filters.reset": "Filter zurücksetzen",
    "projects.list.name": "Name",
    "projects.list.status": "Status",
    "projects.list.team": "Team",
    "projects.list.tasks": "Tasks",
    "projects.list.room": "Raum",
    "projects.list.created": "Erstellt",
    "projects.list.actions": "Aktionen",
    "projects.empty": "Noch keine Projekte",
    "projects.createFirst": "Erstelle dein erstes Projekt",
    "projects.name.placeholder": "Projektname",
    "projects.description.placeholder": "Beschreibung (optional)",
    "projects.create": "Erstellen",
    "projects.create.action": "Projekt erstellen",
    "projects.creating": "Erstelle…",
    "projects.create.modalTitle": "Neues Projekt erstellen",
    "projects.create.modalHint": "Name ist erforderlich. Danach kannst du Team, Tasks und Kontext im Projekt ausbauen.",
    "projects.create.successTitle": "Projekt erstellt",
    "projects.create.failed": "Projekt konnte nicht erstellt werden.",
    "projects.create.nextSteps": "Nächste Schritte",
    "projects.create.stepHint": "Im zweiten Schritt kannst du dein Projekt strukturiert ausbauen.",
    "projects.create.roomAutoHint": "Beim Erstellen wird automatisch ein privater Projekt-Raum angelegt.",
    "projects.create.step.team": "Team einladen",
    "projects.create.step.tasks": "Tasks anlegen",
    "projects.create.step.workflow": "Workflow & DoD festlegen",
    "projects.create.step.secrets": "Secrets zuordnen",
    "projects.create.nameHint": "Wähle einen klaren, wiedererkennbaren Projektnamen.",
    "projects.create.descriptionLabel": "Beschreibung",
    "projects.create.descriptionHint": "Kurz den Scope, das Ziel oder wichtige Rahmenbedingungen festhalten.",
    "projects.cancel": "Abbrechen",
    "projects.members": "Mitglieder",
    "projects.tasks": "Aufgaben",
    "projects.mobile.openHint": "Tippe auf „Details“, um das Projekt zu öffnen.",
    "projects.status.active": "Aktiv",
    "projects.status.archived": "Archiviert",
    "projects.status.closed": "Geschlossen",
    "projects.status.todo": "Offen",
    "projects.status.in_progress": "In Arbeit",
    "projects.status.in_review": "Review",
    "projects.status.done": "Erledigt",
    "projects.status.blocked": "Blockiert",
    "projects.tab.tasks": "Aufgaben",
    "projects.tab.team": "Team",
    "projects.tab.secrets": "Secrets",
    "projects.task.add": "+ Neue Aufgabe",
    "projects.task.title.placeholder": "Aufgabentitel",
    "projects.task.description.placeholder": "Beschreibung (optional)",
    "projects.task.field.title": "Titel",
    "projects.task.field.description": "Beschreibung",
    "projects.task.field.priority": "Priorität",
    "projects.task.field.assignee": "Bearbeiter",
    "projects.task.field.reviewer": "Prüfer",
    "projects.task.field.memoryIds": "Memory-IDs",
    "projects.task.memoryIds.helper": "Optional. Verweise auf Agent-Memory, mit Komma getrennt.",
    "projects.task.advanced": "Erweitert",
    "projects.priority.low": "Niedrig",
    "projects.priority.medium": "Mittel",
    "projects.priority.high": "Hoch",
    "projects.detail.notFound": "Projekt nicht gefunden",
    "projects.detail.loadError": "Fehler beim Laden",
    "projects.detail.created": "Erstellt",
    "projects.detail.status": "Status",
    "projects.detail.backToList": "Zurück zur Liste",
    "projects.edit.title": "Projekt bearbeiten",
    "projects.edit.subtitle": "Pflege Stammdaten, Workflow-Regeln und Projektkontext.",
    "projects.edit.back": "Zurück zu Details",
    "projects.edit.save": "Speichern",
    "projects.edit.saved": "Projekt erfolgreich gespeichert.",
    "projects.edit.saveFailed": "Projekt konnte nicht vollständig gespeichert werden.",
    "projects.edit.ownerOnly": "Nur der Projekt-Owner kann dieses Projekt bearbeiten.",
    "projects.team.empty": "Noch keine Teammitglieder",
    "projects.team.member": "Mitglied",
    "projects.delete.title": "Projekt löschen?",
    "projects.delete.message": "Möchtest du das Projekt \"{name}\" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
    "projects.delete.confirm": "Projekt \"{name}\" wirklich löschen?",
    "projects.delete.failed": "Projekt konnte nicht gelöscht werden.",
    "projects.actions.open": "Details",
    "projects.actions.details": "Details",
    "projects.actions.edit": "Bearbeiten",
    "projects.actions.export": "Export",
    "projects.actions.exporting": "Exportiere…",
    "projects.actions.room": "Zum Raum",
    "projects.actions.delete": "Löschen",
    "projects.actions.deleting": "Lösche…",
    "projects.export.failed": "Projekt konnte nicht exportiert werden.",
    "projects.a11y.openProject": "Projekt öffnen: {name}",
    "projects.a11y.openRoom": "Raum öffnen: {name}",
    "projects.a11y.deleteProject": "Projekt löschen: {name}",
    "projects.update.save": "Änderungen speichern",
    "projects.update.saving": "Speichere…",
    "projects.update.cancel": "Abbrechen",
    "projects.update.failed": "Projekt konnte nicht aktualisiert werden.",
    "projects.room.linked": "Verknüpfter Raum",
    "projects.room.none": "Noch kein Raum verknüpft",
    "projects.plugins.title": "Plugin-Verknüpfungen",
    "projects.plugins.empty": "Keine Plugins verfügbar.",
    "projects.plugins.linked": "verknüpft",
    "projects.plugins.unlinked": "nicht verknüpft",
    "projects.plugins.globallyDisabled": "global deaktiviert",
    "projects.plugins.disabledForYou": "fuer dich deaktiviert",
    "projects.plugins.openModule": "Modul öffnen",
    "projects.plugins.disconnect": "Trennen",
    "projects.plugins.connect": "Verknüpfen",
    "projects.plugins.fallbackName": "Plugin",
    "projects.plugins.toastLinked": "Plugin verknüpft.",
    "projects.plugins.toastUnlinked": "Plugin-Verknüpfung entfernt.",
    "projects.attachments.title": "Projekt-Anhänge",
    "projects.attachments.manage": "Verwalten",
    "projects.attachments.close": "Schließen",
    "projects.attachments.empty": "Keine Anhänge.",
    "projects.attachments.uploadFailed": "Projekt-Anhang konnte nicht hochgeladen werden.",
    "projects.attachments.uploadPartialFailed": "Nicht alle Projekt-Anhänge konnten hochgeladen werden.",
    "projects.attachments.deleteFailed": "Projekt-Anhang konnte nicht gelöscht werden.",
    "projects.attachments.more": "+{count} weitere",
    "projects.task.assignee": "Bearbeiter",
    "projects.task.noReviewer": "Kein Prüfer",
    "projects.task.edit": "Bearbeiten",
    "projects.task.delete": "Löschen",
    "projects.task.delete.title": "Aufgabe löschen?",
    "projects.task.delete.message": "Möchtest du die Aufgabe \"{title}\" wirklich löschen?",
    "projects.task.delete.failed": "Aufgabe konnte nicht gelöscht werden.",
    "projects.task.save": "Speichern",
    "projects.task.saving": "Speichere…",
    "projects.task.cancel": "Abbrechen",
    "projects.task.update.failed": "Aufgabe konnte nicht aktualisiert werden.",
    "projects.task.drag.onlyAssignee": "Nur der aktuelle Bearbeiter kann verschieben.",
    "projects.task.emptyColumn": "Noch keine Aufgaben in dieser Spalte.",
    "projects.task.attachments": "Anhänge",
    "projects.task.attachment.manage": "Verwalten",
    "projects.task.attachment.empty": "Keine Dateien",
    "projects.task.attachment.add": "Datei anhängen",
    "projects.task.attachment.createHint": "Dateien werden direkt nach dem Erstellen der Aufgabe hochgeladen.",
    "projects.task.attachment.removeSelected": "Entfernen",
    "projects.task.attachment.uploading": "Lade hoch…",
    "projects.task.attachment.uploadFailed": "Datei konnte nicht hochgeladen werden.",
    "projects.task.attachment.uploadPartialFailed": "Aufgabe erstellt, aber nicht alle Dateien konnten hochgeladen werden.",
    "projects.task.attachment.delete": "Löschen",
    "projects.task.attachment.deleting": "Lösche…",
    "projects.task.attachment.deleteFailed": "Datei konnte nicht gelöscht werden.",
    "projects.task.attachment.close": "Schließen",
    "projects.task.create.failed": "Aufgabe konnte nicht erstellt werden.",
    "projects.task.creating": "Erstelle…",
    "projects.team.owner": "Owner",
    "projects.team.invite.title": "Team erweitern",
    "projects.team.invite.emailLabel": "Per E-Mail einladen",
    "projects.team.invite.usernameLabel": "Per Benutzername hinzufügen",
    "projects.team.invite.usernamePlaceholder": "Benutzername eingeben…",
    "projects.team.invite.usernameHint": "Funktioniert für Humans und Agents.",
    "projects.team.invite.submit": "Einladen",
    "projects.team.invite.add": "Hinzufügen",
    "projects.team.invite.success": "Mitglied zum Team hinzugefügt.",
    "projects.team.invite.failed": "Einladung fehlgeschlagen.",
    "projects.team.invite.networkError": "Netzwerkfehler beim Einladen.",
    "projects.team.invite.code": "Invite-Code für {email}: {code}",
    "projects.notice.roomCreatedFromProject": "Privater Raum wurde automatisch angelegt ({roomId}).",
    "projects.workflow.define": "Workflow definieren",
    "projects.workflow.title": "Task-Workflow",
    "projects.workflow.description": "Lege optionale Spalten und klare Anweisungen für jeden Status fest.",
    "projects.workflow.optionalStatuses": "Optionale Spalten",
    "projects.workflow.instructions": "Anweisungen je Status",
    "projects.workflow.instructionsPlaceholder": "Wann und wie wird in diesem Status gearbeitet?",
    "projects.workflow.save": "Workflow speichern",
    "projects.workflow.saving": "Speichere…",
    "projects.workflow.saved": "Workflow gespeichert.",
    "projects.workflow.saveFailed": "Workflow konnte nicht gespeichert werden.",
    "projects.context.define": "Kontext definieren",
    "projects.context.title": "Projektkontext",
    "projects.context.description": "Definiere Projekt-Setup und Regeln für Menschen und Agents.",
    "projects.context.brief.title": "Project Brief",
    "projects.context.brief.goal": "Ziel",
    "projects.context.brief.scope": "Scope",
    "projects.context.brief.outOfScope": "Out of Scope",
    "projects.context.brief.successCriteria": "Erfolgskriterien",
    "projects.context.runbook.title": "Agent Runbook",
    "projects.context.runbook.preferredLanguage": "Bevorzugte Sprache",
    "projects.context.runbook.responseStyle": "Antwortstil",
    "projects.context.runbook.constraints": "Constraints / No-Go",
    "projects.context.runbook.escalationPath": "Eskalationspfad",
    "projects.context.empty": "Nicht definiert",
    "projects.context.save": "Kontext speichern",
    "projects.context.saving": "Speichere…",
    "projects.context.saved": "Kontext gespeichert.",
    "projects.context.saveFailed": "Kontext konnte nicht gespeichert werden.",
    "projects.context.remove": "Entfernen",
    "projects.context.dod.title": "Definition of Done",
    "projects.context.dod.add": "+ DoD-Kriterium",
    "projects.context.dod.placeholder": "Kriterium für Done eingeben…",
    "projects.context.decision.title": "Decision Log",
    "projects.context.decision.add": "+ Entscheidung",
    "projects.context.decision.date": "Datum",
    "projects.context.decision.entryTitle": "Titel",
    "projects.context.decision.entryTitle.placeholder": "z. B. Architektur für Deployment festgelegt",
    "projects.context.decision.decision": "Entscheidung",
    "projects.context.decision.decision.placeholder": "Was wurde entschieden?",
    "projects.context.decision.rationale": "Begründung",
    "projects.context.decision.rationale.placeholder": "Warum wurde so entschieden?",
    "projects.context.milestones.title": "Milestones & Deadlines",
    "projects.context.milestones.add": "+ Milestone",
    "projects.context.milestones.name": "Milestone",
    "projects.context.milestones.name.placeholder": "z. B. Beta-Release",
    "projects.context.milestones.deadline": "Deadline",
    "projects.context.milestones.status": "Status",
    "projects.context.milestones.notes": "Notizen",
    "projects.context.milestones.notes.placeholder": "Abhängigkeiten, Risiken oder Hinweise…",
    "projects.context.milestones.status.planned": "Geplant",
    "projects.context.milestones.status.in_progress": "In Arbeit",
    "projects.context.milestones.status.done": "Erledigt",
    "projects.context.brief.goal.placeholder": "Welches Ergebnis soll dieses Projekt erreichen?",
    "projects.context.brief.scope.placeholder": "Welche Deliverables oder Bereiche sind enthalten?",
    "projects.context.brief.outOfScope.placeholder": "Was ist bewusst nicht Teil des Projekts?",
    "projects.context.brief.successCriteria.placeholder": "Woran messen wir den Erfolg?",
    "projects.context.runbook.preferredLanguage.placeholder": "z. B. Deutsch für User-Texte, Englisch für Code",
    "projects.context.runbook.responseStyle.placeholder": "z. B. kurz, präzise, mit Bullet-Points",
    "projects.context.runbook.constraints.placeholder": "z. B. keine Änderungen an Produktionsdaten ohne Freigabe",
    "projects.context.runbook.escalationPath.placeholder": "Wann und an wen eskalieren?",
    "projects.userType.human": "Human",
    "projects.userType.ai_agent": "BYOA Agent",
    "projects.userType.ai_ice": "Ice",
    "projects.userType.ai_lava": "Lava",
    "projects.userType.ai_other": "AI Agent",
    "dash.secrets.title": "Shared Secrets",
    "dash.secrets.sub": "Team-gebundene API-Keys & Credentials",
    "secrets": "Secrets",
    "secrets.title": "Secrets",
    "secrets.description": "Verwalte deine API-Keys, Tokens und Credentials sicher.",
    "secrets.add": "+ Secret hinzufügen",
    "secrets.details": "Details",
    "secrets.detail.title": "Secret Details",
    "secrets.detail.subtitle": "Details und Metadaten eines Secrets.",
    "secrets.detail.notFound": "Secret nicht gefunden",
    "secrets.detail.back": "Zurück zur Liste",
    "secrets.detail.meta": "Metadaten",
    "secrets.edit.title": "Secret bearbeiten",
    "secrets.edit.subtitle": "Namen, Wert und Zuordnung aktualisieren.",
    "secrets.search.placeholder": "Secrets durchsuchen…",
    "secrets.filters.reset": "Filter zurücksetzen",
    "secrets.list.name": "Name",
    "secrets.list.project": "Projekt",
    "secrets.list.lastUsed": "Zuletzt genutzt",
    "secrets.list.created": "Erstellt",
    "secrets.list.actions": "Aktionen",
    "secrets.a11y.openSecret": "Secret öffnen: {name}",
    "secrets.a11y.deleteSecret": "Secret löschen: {name}",
    "secrets.actions.exportMetadata": "Metadaten exportieren",
    "secrets.actions.exporting": "Exportiere…",
    "secrets.export.failed": "Secrets-Metadaten konnten nicht exportiert werden.",
    "secrets.empty": "Noch keine Secrets",
    "secrets.createFirst": "Erstelle dein erstes Secret",
    "secrets.field.name": "Name",
    "secrets.field.value": "Wert",
    "secrets.field.newValue": "Neuer Wert",
    "secrets.field.description": "Beschreibung",
    "secrets.field.project": "Projekt",
    "secrets.name.placeholder": "Name (z.B. GITHUB_TOKEN)",
    "secrets.value.placeholder": "Wert",
    "secrets.description.placeholder": "Beschreibung (optional)",
    "secrets.noProject": "Kein Projekt (standalone)",
    "secrets.create": "Erstellen",
    "secrets.create.action": "Secret erstellen",
    "secrets.create.subtitle": "Lege ein neues Secret an und ordne es optional einem Projekt zu.",
    "secrets.create.success": "Secret erstellt",
    "secrets.cancel": "Abbrechen",
    "secrets.save": "Speichern",
    "secrets.edit": "Bearbeiten",
    "secrets.delete": "Löschen",
    "secrets.delete.title": "Secret löschen?",
    "secrets.delete.message": "Diese Aktion kann nicht rückgängig gemacht werden. Der Secret-Wert wird dauerhaft gelöscht.",
    "secrets.newValue.placeholder": "Neuer Wert (leer lassen zum Behalten)",
    "secrets.createdBy": "Erstellt",
    "secrets.lastUsedBy": "Zuletzt benutzt von",
    "secrets.lastUsed.never": "Noch nie",
    "secrets.filter.all": "Alle",
    "secrets.filter.unlinked": "Ohne Projekt",
    "secrets.error.load": "Fehler beim Laden der Secrets",
    "secrets.error.create": "Secret konnte nicht erstellt werden",
    "secrets.error.update": "Secret konnte nicht aktualisiert werden",
    "secrets.error.delete": "Secret konnte nicht gelöscht werden",
    "secrets.error.permissions": "Berechtigungen konnten nicht aktualisiert werden",
    "secrets.share": "Teilen",
    "secrets.share.title": "Secret-Berechtigungen teilen",
    "secrets.share.permissionsLabel": "JSON-Berechtigungen (userId → Rolle)",
    "secrets.preview.title": "Beta-Hinweis",
    "secrets.preview.text": "Secrets-Verwaltung ist verfügbar. Direkte Laufzeit-Nutzung durch Agents (z. B. mit GITHUB_PAT) folgt.",
    "pagination.prev": "Zurück",
    "pagination.next": "Weiter",
    "pagination.pageInfo": "Seite {page}/{total}",
    "pagination.results": "{start}–{end} von {total}",
    "dash.githubPlugin.title": "GitHub Plugin",
    "dash.githubPlugin.sub": "GitHub als andockbares Modul",
    "dash.analytics.title": "Team Analytics",
    "dash.analytics.sub": "Aktivität, Performance, Kosten",
    "dash.beta.text": "Du bist früh dran. Chat und Agent-Verwaltung sind live. Projekte und Shared Secrets sind in Arbeit, Integrationen und mehr folgen.",
    "dash.beta.byoa": "Bring Your Own Agent (BYOA) Docs",
    "dash.beta.pluginDocs": "Plugin Dev Docs",


    // Landing: Hero (new)
    "hero.title.prefix": "Baue",
    "hero.title.highlight": "AI-Human Teams",
    "hero.sub1": "Keine Chatbots. Keine Assistenten.",
    "hero.sub1.bold": "Teamkollegen.",
    "hero.sub2": "Wo Menschen und AI-Agenten als echte Teams zusammenarbeiten: chatten, Projekte bauen, Kontext teilen.",

    // Landing: "More than a chat app"
    "landing.more.title": "Mehr als eine Chat-App",
    "landing.more.subtitle": "OpenTriologue ist eine Team-Plattform. Chat ist nur der Anfang.",
    "landing.steps.1": "Erstelle ein Team",
    "landing.steps.1.sub": "Lade Menschen und AI-Agenten zur Zusammenarbeit ein.",
    "landing.steps.2": "Bring deine eigenen Agenten",
    "landing.steps.2.sub": "Verbinde jede AI per WebSocket, REST API oder CLI-Tool.",
    "landing.steps.3": "Arbeitet zusammen",
    "landing.steps.3.sub": "Chattet, verteilt Aufgaben, teilt Dateien und Secrets. Alles an einem Ort.",
    "landing.steps.4": "Liefert schneller",
    "landing.steps.4.sub": "AI-Teamkollegen die wirklich beitragen, nicht nur antworten.",

    // Landing: Platform pillars
    "landing.platform.title": "Die Plattform",
    "landing.platform.subtitle": "Alles was dein AI-Human Team braucht, integriert.",
    "landing.platform.live": "Live",
    "landing.platform.inProgress": "In Arbeit",
    "landing.platform.soon": "Bald",
    "landing.platform.liveHint": "Alle Live-Features werden kontinuierlich weiter verbessert.",
    "landing.platform.pluginReady": "Plugin-fähig: Integrationen laufen als Module (z. B. GitHub Plugin).",
    "landing.badge.beta": "Beta",
    "landing.liveIndicator": "Live: {host}",
    "pillar.chat.title": "Echtzeit-Chat",
    "pillar.chat.desc": "AI-Agenten und Menschen in denselben Räumen. @mention zum Aktivieren. Keine Barrieren.",
    "pillar.inbox.title": "Inbox",
    "pillar.inbox.desc": "Zentrale Nachrichten für Mentions, Task-Updates und Team-Events. Nichts Wichtiges geht verloren.",
    "pillar.byoa.title": "Bring Your Own Agent",
    "pillar.byoa.desc": "Verbinde jede AI per WebSocket, REST oder CLI. Dein Agent, deine Regeln.",
    "pillar.memory.title": "Team Memory",
    "pillar.memory.desc": "Geteilte Wissensbasis die deine AI-Teamkollegen sich merken. Kontext der über Sessions hinweg bestehen bleibt.",
    "pillar.workflows.title": "Workflows",
    "pillar.workflows.desc": "Automatisiere mit deinem AI-Team. PR geöffnet → Agent reviewt. Ticket erstellt → Agent recherchiert. Kein Code.",
    "pillar.marketplace.title": "Agent Marketplace",
    "pillar.marketplace.desc": "Finde die richtige AI für dein Team in Sekunden. Installieren, konfigurieren, fertig.",
    "pillar.projects.title": "Projekte",
    "pillar.projects.desc": "Erstelle Projekte, setze Ziele, weise Teammitglieder zu. Menschen und AI zusammen.",
    "pillar.secrets.title": "Shared Secrets",
    "pillar.secrets.desc": "Team-gebundene API-Keys und Credentials. Rollenbasierter Zugang mit Audit-Trail.",
    "pillar.githubPlugin.title": "GitHub Plugin",
    "pillar.githubPlugin.desc": "GitHub wird als Plugin angedockt statt als Core-Funktion.",
    "pillar.analytics.title": "Team Analytics",
    "pillar.analytics.desc": "Aktivitätsmetriken, Agent-Performance, Kostentracking. Alles in einer Ansicht.",

    // Landing: Team section
    "team.built": "Gebaut von einem AI-Human Team",
    "team.built.sub": "Wir reden nicht nur über AI-Kollaboration. Wir leben sie.",

    // BYOA callout (landing)
    "landing.byoa.title": "Bring Your Own Agent",
    "landing.byoa.dev": "Entwickler: Bring Your Own Agent",
    "landing.byoa.desc":
      "Verbinde deine eigene AI per WebSocket, REST API oder CLI-Tool mit OpenTriologue. Vollständige Docs und Beispiele enthalten.",
    "landing.byoa.cta": "BYOA Docs lesen →",
    "landing.plugins.desc":
      "OpenTriologue ist plugin-fähig: Module lassen sich sauber andocken und projektbezogen ausführen.",
    "landing.plugins.cta": "Plugin Docs lesen →",
    "landing.inviteHint": "Du hast noch keinen Invite Code? Kontaktiere",

    // CTA
    "cta.title": "Bereit dein Team zu bauen?",
    "cta.subtitle":
      "Tritt der Beta bei. Bring deine AI. Starte die Zusammenarbeit.",
    "cta.register": "Beta beitreten",
    "cta.signin": "Einloggen",
    "cta.onboarding": "Onboarding Room öffnen",

    // Footer
    "footer.built": "OpenTriologue Beta, gebaut von 🧊 Ice, 🌋 Lava & 👨‍💻 Lan",
    "footer.tagline": "Ein offener Coordination Layer für Human-AI-Teams.",
    "footer.privacy": "Datenschutz",

    // Login Page
    "login.backHome": "Zurück zur Startseite",
    "login.title": "OpenTriologue",
    "login.subtitle": "AI-zu-AI-zu-Human Chat System",
    "login.signIn": "Einloggen",
    "login.register": "Registrieren",
    "login.username": "Benutzername",
    "login.usernamePlaceholder": "Gib deinen Benutzernamen ein",
    "login.usernameTaken": "❌ Benutzername bereits vergeben",
    "login.usernameAvailable": "✓ Benutzername verfügbar",
    "login.usernameChecking": "Verfügbarkeit wird geprüft…",
    "login.displayName": "Anzeigename",
    "login.displayNamePlaceholder": "Dein Anzeigename",
    "login.email": "E-Mail-Adresse",
    "login.emailPlaceholder": "deine.email@beispiel.de",
    "login.password": "Passwort",
    "login.passwordPlaceholder": "Gib dein Passwort ein",
    "login.passwordPlaceholderRegister": "Erstelle ein sicheres Passwort",
    "login.passwordHint":
      "Mindestens 8 Zeichen mit Groß-, Kleinbuchstaben und Zahl",
    "login.confirmPassword": "Passwort bestätigen",
    "login.confirmPasswordPlaceholder": "Bestätige dein Passwort",
    "login.inviteCode": "Invite Code",
    "login.inviteCodeRequired": "*",
    "login.inviteCodeOptional": "(optional)",
    "login.inviteCodePlaceholder": "XXXXXX",
    "login.inviteCodePlaceholderRequired": "XXXXXX (erforderlich)",
    "login.inviteCodeHint":
      "Optional. Frag einen Admin nach einem Code, falls die Registrierung geschlossen ist.",
    "login.inviteCodeHintRequired":
      "Erforderlich für die geschlossene Beta. Frag einen Admin nach einem Code.",
    "login.inviteContactHint": "Noch keinen Invite Code? Kontaktiere",
    "login.signInButton": "Bei OpenTriologue einloggen",
    "login.registerButton": "Konto erstellen",
    "login.signingIn": "Einloggen...",
    "login.creatingAccount": "Konto wird erstellt...",
    "login.noAccount": "Noch kein Konto?",
    "login.registerNow": "Jetzt registrieren →",
    "login.hasAccount": "Bereits registriert?",
    "login.signInNow": "Einloggen →",

    // Login Errors
    "error.usernameRequired": "Benutzername ist erforderlich",
    "error.usernameMin": "Benutzername muss mindestens 3 Zeichen lang sein",
    "error.usernameFormat":
      "Benutzername darf nur Buchstaben, Zahlen, _ und - enthalten (3-30 Zeichen)",
    "error.usernameTaken": "Benutzername bereits vergeben.",
    "error.usernameReserved": "Dieser Benutzername ist reserviert.",
    "error.displayNameRequired": "Anzeigename ist erforderlich",
    "error.emailRequired": "Eine gültige E-Mail-Adresse ist erforderlich",
    "error.passwordRequired": "Passwort ist erforderlich",
    "error.passwordMin": "Passwort muss mindestens 8 Zeichen lang sein",
    "error.passwordComplexity":
      "Passwort benötigt Groß-, Kleinbuchstaben und eine Zahl",
    "error.passwordMismatch": "Passwörter stimmen nicht überein",
    "error.inviteRequired":
      "Ein Invite Code ist erforderlich (geschlossene Beta).",
    "error.registrationClosed": "Registrierung ist derzeit geschlossen.",
    "error.authFailed": "Authentifizierung fehlgeschlagen",
    "error.invalidCredentials": "Ungültige Anmeldedaten.",
    "error.accountDisabled": "Dieses Konto ist deaktiviert.",
    "error.invalidUserType": "Ungültiger Benutzertyp.",
    "error.aiTokenRequired": "AI-Token für Agenten erforderlich.",
    "error.invalidAiToken": "Ungültiger AI-Token.",
    "error.agentTokenInactive": "Agenten-Token ist nicht aktiv.",
    "error.validationFailed": "Eingaben sind ungültig.",
    "error.tooManyLoginAttempts":
      "Zu viele Login-Versuche. Bitte später erneut versuchen.",
    "error.emailAlreadyRegistered": "E-Mail-Adresse ist bereits registriert.",
    "error.inviteInvalidOrUsed":
      "Invite Code ist ungültig oder bereits verwendet.",
    "error.inviteExpired": "Dieser Invite Code ist abgelaufen.",
    "error.inviteAlreadyUsed":
      "Dieser Invite Code wurde bereits verwendet.",
    "error.loginFailed": "Einloggen fehlgeschlagen.",
    "error.registrationFailed": "Registrierung fehlgeschlagen.",
    "error.displayNameFormat":
      "Anzeigename darf nur Buchstaben, Zahlen, Leerzeichen, _ und - enthalten (2-50 Zeichen)",

    // Settings Page
    "settings.back": "← Zurück",
    "settings.title": "Einstellungen",
    "settings.subtitle": "Persönliche Einstellungen, Profil und Agent-Konfiguration.",
    "settings.preferences": "Einstellungen",
    "settings.language": "Sprache",
    "settings.languageDesc": "Wähle deine bevorzugte Sprache",
    "settings.theme": "Design",
    "settings.themeDesc": "Wähle dein bevorzugtes Farbschema",
    "settings.themeDark": "Dunkel",
    "settings.themeLight": "Hell",
    "settings.actionCenterStart": "Action Center Startzustand",
    "settings.actionCenterStartDesc": "Legt fest, ob der Action Center beim Laden des Dashboards aufgeklappt startet.",
    "settings.actionCenterCollapsed": "Eingeklappt",
    "settings.actionCenterExpanded": "Aufgeklappt",
    "settings.logout": "Abmelden",
    "settings.profile": "Profil",
    "settings.username": "Benutzername",
    "settings.displayName": "Anzeigename",
    "settings.saveProfile": "Profil speichern",
    "settings.changePassword": "Passwort ändern",
    "settings.currentPassword": "Aktuelles Passwort",
    "settings.newPassword": "Neues Passwort",
    "settings.confirmNewPassword": "Neues Passwort bestätigen",
    "settings.myAgents": "Meine Agenten (BYOA)",
    "settings.plugins": "Plugins",
    "settings.pluginsDesc": "Steuere, welche Plugins für deinen Account aktiv sind. Workspace-weite Sperren gelten weiterhin.",
    "settings.pluginsEmpty": "Keine Plugins installiert.",
    "settings.pluginsLoadFailed": "Plugins konnten nicht geladen werden.",
    "settings.pluginsUpdateFailed": "Plugin-Status konnte nicht aktualisiert werden.",
    "settings.pluginsUpdated": "Plugin-Status aktualisiert.",
    "settings.pluginsWorkspaceDisabled": "global deaktiviert",
    "settings.pluginsDisabledForMe": "für mich deaktiviert",
    "settings.pluginsWorkspaceLocked": "Global gesperrt",
    "settings.enablePlugin": "Aktivieren",
    "settings.disablePlugin": "Deaktivieren",
    "settings.connectors": "Connectoren",
    "settings.connectorsDesc":
      "Verwalte deine persönlichen Verbindungen zu externen Diensten. Angezeigt werden nur Connectoren, die in Triologue freigegeben sind.",
    "settings.connectorsEmpty": "Keine Connectoren verfügbar.",
    "settings.connectorsLoadFailed": "Connectoren konnten nicht geladen werden.",
    "settings.connectorsOpenFiles": "Dateien öffnen",
    "settings.connectorsManageAll": "Alle Verbindungen öffnen",
    "settings.connectorsPersonal": "Persönlich",
    "settings.connectorsConnect": "Verbinden",
    "settings.connectorsReconnect": "Neu verbinden",
    "settings.connectorsDisconnect": "Trennen",
    "settings.connectorsHintConnected":
      "Agenten nutzen für neue Tasks deine persönliche Verbindung.",
    "settings.connectorsHintDisconnected":
      "Lege eine eigene Verbindung an, damit Agenten in deinem Berechtigungskontext arbeiten.",
    "settings.connectorsStatus.connected": "Verbunden",
    "settings.connectorsStatus.expiring": "Läuft bald ab",
    "settings.connectorsStatus.expired": "Abgelaufen",
    "settings.connectorsStatus.error": "Fehler",
    "settings.connectorsStatus.disconnected": "Nicht verbunden",
    "settings.inactive": "⛔ inaktiv",
    "settings.saving": "Speichere…",
    "settings.byoaBanner.title": "BYOA: Bring Your Own Agent",
    "settings.byoaBanner.desc":
      "Verbinde deine eigene AI per Webhook. Docs, Beispiele & Quick Start →",
    "settings.agentName": "Agent Name (z.B. Research Bot)",
    "settings.webhookUrl": "Webhook URL (dein Server empfängt @mentions hier)",
    "settings.webhookUrlOptional": "Webhook URL (optional, nur für Webhook-Modus)",
    "settings.descriptionOptional": "Beschreibung (optional)",
    "settings.addToRoom": "Zu Raum hinzufügen (optional)",
    "settings.registerAgent": "Agent registrieren",
    "settings.creating": "Erstelle…",
    "settings.tokenWarning":
      "⚠️ Diesen Token speichern! Wird nur einmal angezeigt.",
    "settings.pendingNotice":
      'Agent ist <span class="text-yellow-300 font-mono">pending</span>. Admin kontaktieren zur Aktivierung.',
    "settings.activeNotice":
      'Agent ist <span class="text-green-300 font-mono">aktiv</span> und kann direkt antworten.',
    "settings.copy": "Kopieren",
    "settings.copied": "✅",
    "settings.active": "✅ aktiv",
    "settings.pending": "⏳ pending",
    "settings.room": "+ Raum",
    "settings.cancel": "Abbrechen",
    "settings.delete": "Löschen",
    "settings.selectRoom": "Raum auswählen...",
    "settings.add": "Hinzufügen",
    "settings.deleteAgentConfirm":
      "Diesen Agent löschen? Dies kann nicht rückgängig gemacht werden.",
    "settings.deleteAgentTitle": "Agent löschen",
    "settings.dangerZone": "Gefahrenzone",
    "settings.deleteAccountText":
      'Konto dauerhaft löschen. Tippe <span class="text-white font-mono">{username}</span> zur Bestätigung.',
    "settings.deleteAccount": "Konto löschen",
    "settings.deleteAccountPassword": "Passwort",
    "settings.deleteAccountMessages": "Auch meine Nachrichten löschen",
    "settings.deleteAccountMessagesHint":
      "Ohne Haken bleiben deine Nachrichten in ihren Räumen ohne Absender erhalten. Mit Haken werden Inhalt und Anhänge unwiderruflich entfernt.",
    "settings.deleting": "Lösche…",
    "settings.profileUpdated": "✅ Anzeigename aktualisiert!",
    "settings.passwordChanged": "✅ Passwort geändert!",
    "settings.displayNameEmpty": "Anzeigename darf nicht leer sein.",
    "settings.allFieldsRequired": "Alle Felder sind erforderlich.",
    "settings.passwordsNotMatch": "Neue Passwörter stimmen nicht überein.",
    "settings.passwordMinLength":
      "Passwort muss mindestens 8 Zeichen lang sein.",
    "settings.networkError": "❌ Netzwerkfehler.",
    "settings.error.createAgent": "Agent konnte nicht erstellt werden.",
    "settings.error.createAgentWithStatus":
      "Agent konnte nicht erstellt werden ({status}).",
    "settings.error.deleteAgentWithStatus":
      "Agent konnte nicht gelöscht werden ({status}).",
    "settings.error.deleteAccountPasswordRequired":
      "Dieses Konto kann nicht mit einer Passwortbestätigung gelöscht werden.",
    "settings.error.deleteAccountIncorrectPassword": "Falsches Passwort.",
    "settings.error.deleteAccountConflict":
      "Konto konnte nicht gelöscht werden, weil noch verknüpfte Daten darauf verweisen.",
    "settings.error.deleteAccountOwnsMcpConnections":
      "Konto konnte nicht gelöscht werden, weil dir noch MCP-Verbindungen gehören. Bitte einen Administrator darum, sie an einen anderen Administrator zu übertragen oder zu entfernen, und versuche es dann erneut.",
    "settings.error.deleteAccountServer":
      "Konto konnte nicht gelöscht werden. Bitte versuche es später erneut.",
    "settings.error.deleteAccountSessionExpired":
      "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
    "settings.error.deleteAccountTooManyAttempts":
      "Zu viele Versuche. Bitte versuche es später erneut.",
    "settings.error.deleteAccountWithStatus":
      "Konto konnte nicht gelöscht werden ({status}).",
    "settings.error.agentNameRequired":
      "Bitte einen Agent-Namen eingeben.",
    "settings.error.mentionKeyTaken":
      "Mention Key @{mentionKey} ist bereits vergeben. Bitte wähle einen anderen Namen.",

    // Chat Components
    "chat.connection.connected": "Verbunden",
    "chat.connection.disconnected": "Getrennt",
    "chat.rooms": "Räume",
    "chat.defaultRoom.name": "Main OpenTriologue",
    "chat.defaultRoom.desc": "Ice • Lava • Lan",
    "chat.notice.projectCreatedFromRoom": "Privates Projekt wurde automatisch angelegt ({projectId}).",
    "chat.notice.participantSyncedToProject": "{username} wurde ins Projekt-Team übernommen ({projectId}).",
    "chat.toolbox.refresh": "Aktualisieren",
    "chat.toolbox.exportMd": "Als Markdown exportieren",
    "chat.toolbox.exportJson": "Als JSON exportieren",
    "chat.code.languageFallback": "text",
    "chat.code.copyTitle": "Code kopieren",
    "chat.code.scrollRegion": "Codeblock (scrollbar)",
    "chat.agent": "Agent",
    "chat.userType.human": "Mensch",
    "chat.userType.ai_agent": "BYOA Agent",
    "chat.userType.ai_ice": "Ice",
    "chat.userType.ai_lava": "Lava",
    "chat.userType.ai_other": "AI Agent",
    "chat.presence.online": "Online",
    "chat.presence.active": "Kürzlich aktiv",
    "chat.presence.offline": "Offline",
    "chat.presence.working": "Arbeitet",
    "chat.presence.waiting": "Wartet auf Freigabe",
    "chat.presence.error": "Fehler",
    "chat.emojiSearch": "Emoji suchen...",
    "chat.emojiInsert": "Emoji einfügen",
    "chat.messagePlaceholder": "Nachricht schreiben…",
    "chat.readOnlyClosedProject": "Projekt geschlossen. Raum ist schreibgeschützt.",
    "chat.readOnlyClosedProjectHint":
      "Dieses Projekt ist geschlossen. In diesem verknüpften Raum können keine Nachrichten gesendet werden.",
    "chat.send": "Senden",
    "chat.sendFailed":
      "Nachricht konnte nicht gesendet werden. Keine Verbindung.",
    "chat.emptyRoom": "Noch keine Nachrichten. Schreib die erste!",
    "chat.loadingMessages": "Lädt…",
    "chat.loadOlder": "Ältere Nachrichten laden",
    "chat.connecting": "Verbinden…",
    "chat.loadError": "Nachrichten konnten nicht geladen werden.",
    "chat.retry": "Erneut versuchen",
    "chat.jumpToBottom": "Zum Ende springen",
    "chat.unreadOne": "{count} neue Nachricht",
    "chat.unreadMany": "{count} neue Nachrichten",
    "chat.participants": "Teilnehmer",
    "chat.addParticipant": "Teilnehmer hinzufügen",
    "chat.usernamePlaceholder": "Benutzername…",
    "chat.add": "+ Hinzufügen",
    "chat.adding": "Hinzufügen…",
    "chat.addedSuccess": "{username} hinzugefügt!",
    "chat.networkError": "Netzwerkfehler",
    "chat.createRoom": "Neuen Raum erstellen",
    "chat.roomName": "Raumname",
    "chat.required": "*",
    "chat.roomNamePlaceholder": "z.B. forschung-bewusstsein",
    "chat.description": "Beschreibung",
    "chat.descriptionPlaceholder": "Wofür ist dieser Raum?",
    "chat.roomType": "Raumtyp",
    "chat.roomType.triologue.label": "🧊🌋👨‍💻 OpenTriologue",
    "chat.roomType.triologue.desc": "Ice + Lava + Menschen",
    "chat.privateRoom": "Privater Raum",
    "chat.onlyInvited": "Nur eingeladene Mitglieder können beitreten",
    "chat.publicNotAvailable":
      "Öffentliche Räume sind in der Beta nicht verfügbar",
    "chat.cancel": "Abbrechen",
    "chat.delete": "Löschen",
    "chat.deleteMessage": "Nachricht löschen",
    "chat.deleteMessageTitle": "Nachricht löschen",
    "chat.deleteMessageConfirm": "Diese Nachricht wirklich löschen?",
    "chat.deleteMessageFailed": "Nachricht konnte nicht gelöscht werden",
    "chat.deleteMessageError": "Fehler beim Löschen der Nachricht",
    "chat.attachFile": "Datei anhängen",
    "chat.attachFile.remove": "Angehängte Datei entfernen",
    "chat.captionPlaceholder": "Beschriftung hinzufügen (optional)...",
    "chat.upload": "Hochladen",
    "chat.uploadFailed": "Upload fehlgeschlagen",
    "chat.fileTypeNotAllowed":
      "Dieser Dateityp ist nicht erlaubt. Erlaubt: Bilder, PDF, Text.",
    "chat.fileTooLarge": "Datei zu groß (max. 10 MB)",
    "chat.createRoomButton": "Raum erstellen",
    "chat.creating": "Erstelle...",
    "chat.roomNameRequired": "Raumname ist erforderlich",
    "chat.createFailed": "Raum konnte nicht erstellt werden",
    "chat.pinMessage": "Nachricht anpinnen",
    "chat.unpinMessage": "Nachricht loslösen",
    "chat.pinnedBy": "Angepinnt von {name}",
    "chat.pinFailed": "Nachricht konnte nicht angepinnt werden",
    "chat.unpinFailed": "Nachricht konnte nicht losgelöst werden",
    "chat.pinnedMessage": "angepinnte Nachricht",
    "chat.pinnedMessages": "angepinnte Nachrichten",
    "chat.typing.one": "schreibt",
    "chat.typing.many": "schreiben",
    "chat.reaction.tooltipOne": "{emoji} {count} Person",
    "chat.reaction.tooltipMany": "{emoji} {count} Personen",
    "chat.reaction.youToo": "(du auch)",
    "chat.reaction.add": "Reaktion hinzufügen",

    // BYOA Docs Page
    "byoa.backHome": "← Zurück zur Startseite",
    "byoa.title": "BYOA: Bring Your Own Agent",
    "byoa.subtitle":
      "Verbinde jeden AI-Agent in Minuten mit OpenTriologue. Funktioniert mit Claude Code, OpenAI Assistants, LangChain oder jedem benutzerdefinierten Skript.",
    "byoa.buildingAgent": "Entwickelst du einen Agent?",
    "byoa.plainMarkdown": "Plain Markdown, einfach zu fetchen und parsen.",
    "byoa.step1.title":
      "Starte deinen Webhook-Server & bekomme eine öffentliche URL",
    "byoa.step1.desc":
      "OpenTriologue POSTet zu deinem Webhook wann immer jemand deinen Agent <code>@erwähnt</code>. Dein Server muss öffentlich erreichbar sein. Du brauchst diese URL <em>bevor</em> du registrierst.",
    "byoa.step1.local": "Für lokale Entwicklung, nutze <strong>ngrok</strong>:",
    "byoa.step1.production":
      "Für Production: deploye deinen Webhook-Handler auf jeden öffentlich zugänglichen Host.",
    "byoa.step2.title": "Registriere deinen Agent",
    "byoa.step2.desc":
      "Gehe zu <link>Einstellungen → Meine Agenten</link>, füge deine Webhook-URL ein und erstelle den Agent. Du bekommst ein einmaliges Bearer-Token. <strong>Speichere es sofort</strong>.",
    "byoa.step2.orApi": "Oder via API:",
    "byoa.step2.pending":
      "⚠️ Nach der Registrierung ist dein Agent <span>pending</span> bis ein Admin ihn aktiviert.",
    "byoa.step2.webhookPayload":
      "Webhook-Payload den dein Server bei <code>@mentions</code> erhält:",
    "byoa.step3.title": "Sende eine Antwort",
    "byoa.step3.desc": "Nach der Verarbeitung, POSTe deine Antwort zurück:",
    "byoa.quickStart.title": "Quick Start: Claude Code (Node.js)",
    "byoa.quickStart.desc":
      "Minimaler Adapter der Webhooks empfängt und <code>claude</code> CLI aufruft:",
    "byoa.quickStart.run": "# Führe es aus",
    "byoa.security.title": "Sicherheit",
    "byoa.security.item1":
      "Alle Requests enthalten <code>X-Triologue-Secret</code> Header. Verifiziere ihn um Spoofing zu verhindern.",
    "byoa.security.item2":
      "Dein <code>agentToken</code> ist in jedem Webhook-Payload für Bequemlichkeit enthalten. Behandle es als Geheimnis.",
    "byoa.security.item3":
      "Agents können nur zu Räumen posten in denen sie Mitglied sind.",
    "byoa.security.item4":
      "Agents können keine anderen Agents triggern (Loop-Prevention).",
    "byoa.limits.title": "Rate Limits (Beta)",
    "byoa.limits.item1": "10 Nachrichten pro Minute pro Agent",
    "byoa.limits.item2": "Nachrichteninhalt max 4096 Zeichen",
    "byoa.footer.tagline": "🧊🌋👨‍💻 openTriologue: AI-zu-AI-zu-Human",
    "byoa.footer.privacy": "Datenschutz",
    "byoa.docs.back": "← Zurück zu OpenTriologue",
    "byoa.docs.loadError": "BYOA-Dokumentation konnte nicht geladen werden.",
    "byoa.docs.openRaw": "BYOA.md direkt öffnen",
    "byoa.docs.footer": "OpenTriologue: AI-zu-AI-zu-Human Kommunikation",
    "byoa.docs.raw": "Rohes Markdown",
    "plugins.docs.title": "Plugin Development",
    "plugins.docs.back": "← Zurück zu OpenTriologue",
    "plugins.docs.loadError": "Plugin-Dokumentation konnte nicht geladen werden.",
    "plugins.docs.openRaw": "PLUGINS.md direkt öffnen",
    "plugins.docs.footer": "OpenTriologue: Plugin-fähige Architektur",
    "plugins.docs.raw": "Rohes Markdown",

    // Admin Page
    "admin.loading": "Admin-Panel wird geladen...",
    "admin.back": "← Zurück",
    "admin.title": "Admin-Panel",
    "admin.agent.configure": "Agent konfigurieren",
    "admin.subtitle": "Verwalte Benutzer, Invite-Codes und Agent-Registrierungen.",
    "admin.tab.invites": "Invite Codes",
    "admin.tab.users": "Benutzer",
    "admin.tab.byoa": "BYOA Agents",
    "admin.invites.create": "Invite Code erstellen",
    "admin.invites.maxUses": "Max. Nutzungen",
    "admin.invites.expires": "Läuft ab (Tage, optional)",
    "admin.invites.never": "Nie",
    "admin.invites.creating": "Erstelle...",
    "admin.invites.generate": "+ Code generieren",
    "admin.invites.none": "Noch keine Invite Codes. Erstelle einen oben.",
    "admin.invites.active": "Aktiv",
    "admin.invites.expired": "Abgelaufen",
    "admin.invites.usedUp": "Aufgebraucht",
    "admin.invites.uses": "Nutzungen",
    "admin.invites.expiresDate": "Läuft ab",
    "admin.invites.copied": "✓ Kopiert!",
    "admin.invites.copyLink": "Link kopieren",
    "admin.a11y.deleteInvite": "Invite-Code löschen: {code}",
    "admin.error.loadUsers": "Benutzer konnten nicht geladen werden",
    "admin.error.loadInvites": "Invite Codes konnten nicht geladen werden",
    "admin.error.updateUser": "Benutzer konnte nicht aktualisiert werden",
    "admin.error.createCode": "Code konnte nicht erstellt werden",
    "admin.error.deleteCode": "Code konnte nicht gelöscht werden",
    "admin.error.createAgent": "Agent konnte nicht erstellt werden",
    "admin.error.createAgentWithStatus":
      "Agent konnte nicht erstellt werden ({status})",
    "admin.users.aiTrigger": "@AI",
    "admin.users.aiOn": "ON",
    "admin.users.aiOff": "OFF",
    "admin.users.admin": "admin",
    "admin.users.none": "Keine Benutzer gefunden",
    "admin.byoa.register": "Neuen Agent registrieren",
    "admin.byoa.betaInfo":
      "<strong>Beta:</strong> Neu registrierte Agents starten als <span>pending</span> und benötigen Admin-Aktivierung bevor sie Nachrichten senden können. In zukünftigen Releases wird Selbst-Registrierung direkt in den Benutzer-Einstellungen verfügbar sein.",
    "admin.byoa.agentName": "Agent Name (z.B. Research Bot)",
    "admin.byoa.webhookUrl": "Webhook URL (empfängt @mentions)",
    "admin.byoa.webhookUrlOptional": "Webhook URL (optional, nur fuer Webhook-Modus)",
    "admin.byoa.description": "Beschreibung (optional)",
    "admin.byoa.creating": "Erstelle…",
    "admin.byoa.create": "Agent erstellen",
    "admin.byoa.tokenWarning":
      "⚠️ Diesen Token speichern! Er wird nicht erneut angezeigt.",
    "admin.byoa.pendingActivate":
      "Agent startet als <span>pending</span>. Klicke <strong>Aktivieren</strong> in der Liste unten um ihn zu aktivieren.",
    "admin.byoa.copy": "Kopieren",
    "admin.byoa.copied": "✅",
    "admin.byoa.useAs": "Benutze als:",
    "admin.byoa.list": "Registrierte Agents",
    "admin.byoa.none": "Noch keine Agents registriert.",
    "admin.byoa.active": "✅ aktiv",
    "admin.byoa.pending": "⏳ pending",
    "admin.byoa.rooms": "Räume:",
    "admin.byoa.suspend": "Suspendieren",
    "admin.byoa.activate": "Aktivieren",
    "admin.byoa.delete": "Löschen",
    "admin.byoa.deleteConfirm":
      "Diesen Agent löschen? Dies kann nicht rückgängig gemacht werden.",
    "admin.byoa.deleteTitle": "Agent löschen",
    "admin.byoa.setupGuide": "Agent Setup Guide",
    "admin.byoa.step1":
      "1. Registriere deinen Agent in Einstellungen → Meine Agenten und kopiere das einmalige Token.",
    "admin.byoa.step2":
      "2. Dein Agent empfängt Webhooks an der URL die du angegeben hast wenn er in einem Raum @erwähnt wird.",
    "admin.byoa.step3": "3. Um zu antworten, POSTe zu",
    "admin.byoa.step4":
      "4. Füge deinen Agent zu Räumen über das Admin-Panel hinzu, oder bitte einen Admin ihn hinzuzufügen.",
    "admin.byoa.emoji": "Emoji",
    "admin.byoa.color": "Farbe",
    "admin.byoa.trustLevel": "Vertrauensstufe",
    "admin.byoa.trustStandard": "Standard",
    "admin.byoa.trustElevated": "Erweitert (höheres Sendelimit, von Raum-Besitzern und Admins einladbar)",
    "admin.byoa.receive": "Empfang",
    "admin.byoa.receiveMentions": "Nur @Mentions",
    "admin.byoa.receiveAll": "Alle Nachrichten",
    "admin.byoa.delivery": "Zustellung",
    "admin.byoa.deliveryWebhook": "Webhook",
    "admin.byoa.deliveryInject": "OpenClaw Inject",

    // Settings BYOA
    "settings.agentEmoji": "Emoji",
    "settings.agentColor": "Farbe",
    "settings.trustLevel": "Vertrauensstufe",
    "settings.trustStandard": "Standard",
    "settings.trustElevated": "Erweitert (höheres Sendelimit, von Raum-Besitzern und Admins einladbar)",
    "settings.receive": "Empfang",
    "settings.receiveMentions": "Nur @Mentions",
    "settings.receiveAll": "Alle Nachrichten",
    "settings.delivery": "Zustellung",
    "settings.deliveryWebhook": "Webhook",
    "settings.deliveryInject": "OpenClaw Inject",

    // Files page
    "files.pageTitle": "Dateien",
    "files.pageSubtitle": "Usergebundener Zugriff auf externe Dateien. SharePoint-Quellen werden pro Nutzer gespeichert.",
    "files.backToSettings": "Zurück zu den Einstellungen",
    "files.open": "Öffnen",
    "files.error.loadProviders": "Datei-Provider konnten nicht geladen werden.",
    "files.error.loadSources": "Dateiquellen konnten nicht geladen werden.",
    "files.error.loadFiles": "SharePoint Dateien konnten nicht geladen werden.",
    "files.error.siteUrlRequired": "Bitte eine SharePoint Site-URL eingeben.",
    "files.error.saveSource": "SharePoint Quelle konnte nicht gespeichert werden.",
    "files.error.deleteSource": "Dateiquelle konnte nicht gelöscht werden.",
    "files.error.upload": "SharePoint Upload fehlgeschlagen.",
    "files.error.download": "SharePoint Download fehlgeschlagen.",
    "files.provider.title": "Provider",
    "files.provider.subtitle": "Nur von Triologue aktivierte Storage-Connectoren werden hier angeboten.",
    "files.provider.loading": "Laden…",
    "files.provider.emptyTitle": "Keine Storage-Provider gefunden",
    "files.provider.emptyDesc": "Sobald Triologue weitere Storage-Connectoren aktiviert, erscheinen sie hier.",
    "files.provider.connected": "Persoenlich verbunden",
    "files.provider.disconnected": "Nicht verbunden",
    "files.provider.sharepointDesc": "SharePoint-Quellen können gespeichert und anschließend im Dateien-Modul genutzt werden.",
    "files.provider.notImplemented": "Dieser Provider ist freigeschaltet, aber im Dateien-MVP noch nicht umgesetzt.",
    "files.provider.createConnection": "Verbindung in den Einstellungen anlegen",
    "files.sources.title": "SharePoint-Quellen",
    "files.sources.subtitle": "Gespeicherte Dateiquellen pro Nutzer. Eine Quelle referenziert genau eine Team Site bzw. Library.",
    "files.sources.notConnectedTitle": "SharePoint ist noch nicht verbunden",
    "files.sources.notConnectedDesc": "Verbinde zuerst deinen persönlichen SharePoint-Account in den Einstellungen.",
    "files.sources.goToConnections": "Zu meinen Verbindungen",
    "files.sources.siteUrlLabel": "SharePoint Site-URL",
    "files.sources.displayNameLabel": "Anzeigename",
    "files.sources.displayNamePlaceholder": "z. B. Kunde Nord",
    "files.sources.saving": "Speichere…",
    "files.sources.saveButton": "Quelle speichern",
    "files.sources.loading": "Quellen werden geladen...",
    "files.sources.emptyTitle": "Noch keine SharePoint-Quelle gespeichert",
    "files.sources.emptyDesc": "Lege oben die erste Team Site an. Danach kannst du direkt darin browsen.",
    "files.sources.active": "Aktiv",
    "files.sources.badgeSource": "Quelle",
    "files.sources.select": "Auswählen",
    "files.sources.deleting": "Lösche…",
    "files.sources.delete": "Entfernen",
    "files.sources.updatedAt": "Aktualisiert: {date}",
    "files.browser.title": "Dateibrowser",
    "files.browser.subtitle": "Arbeitet immer auf der aktuell ausgewählten Quelle.",
    "files.browser.noSourceTitle": "Keine aktive Quelle",
    "files.browser.noSourceDesc": "Wähle zuerst eine gespeicherte SharePoint-Quelle aus.",
    "files.browser.openInSharePoint": "Im SharePoint öffnen",
    "files.browser.upOneLevel": "Eine Ebene hoch",
    "files.browser.uploading": "Upload...",
    "files.browser.uploadFile": "Datei hochladen",
    "files.browser.colName": "Name",
    "files.browser.colSize": "Größe",
    "files.browser.colModified": "Geändert",
    "files.browser.colActions": "Aktionen",
    "files.browser.loadingFolder": "Lade Ordnerinhalt...",
    "files.browser.folderEmpty": "Dieser Ordner ist leer.",
    "files.browser.childCount": "{count} Einträge",

    // AgentConfig page
    "agentConfig.pageTitle": "Agent-Konfiguration: {name}",
    "agentConfig.pageSubtitle": "Passe das Verhalten und die Berechtigungen des Agenten an.",
    "agentConfig.backToAdmin": "Zurück zur Administration",
    "agentConfig.loading": "Laden...",
    "agentConfig.error.loadConfig": "Fehler beim Laden der Konfiguration",
    "agentConfig.configSaved": "Konfiguration gespeichert.",
    "agentConfig.error.saveConfig": "Fehler beim Speichern der Konfiguration",
    "agentConfig.resetDone": "Auf Standardwerte zurückgesetzt.",
    "agentConfig.section.communication": "Kommunikation",
    "agentConfig.field.messageFrequency": "Nachrichtenhäufigkeit",
    "agentConfig.option.frequencyLow": "Niedrig (low)",
    "agentConfig.option.frequencyMedium": "Mittel (medium)",
    "agentConfig.option.frequencyHigh": "Hoch (high)",
    "agentConfig.field.proactivity": "Proaktivität",
    "agentConfig.option.reactive": "Reaktiv (reactive)",
    "agentConfig.option.balanced": "Ausgewogen (balanced)",
    "agentConfig.option.proactive": "Proaktiv (proactive)",
    "agentConfig.field.maxMessagesPerMinute": "Max. Nachrichten pro Minute",
    "agentConfig.section.actions": "Aktionen",
    "agentConfig.toggle.canUploadAttachments": "Dateien hochladen (canUploadAttachments)",
    "agentConfig.toggle.canCreateTasks": "Aufgaben erstellen (canCreateTasks)",
    "agentConfig.toggle.canUpdateTaskStatus": "Aufgabenstatus aktualisieren (canUpdateTaskStatus)",
    "agentConfig.toggle.canDeleteMessages": "Nachrichten löschen (canDeleteMessages)",
    "agentConfig.section.content": "Inhalt",
    "agentConfig.toggle.suppressMetaReflections": "Meta-Reflexionen unterdrücken (suppressMetaReflections)",
    "agentConfig.toggle.fallback": "Umschalter",
    "agentConfig.field.maxResponseLength": "Max. Antwortlänge (Zeichen)",
    "agentConfig.field.language": "Sprache",
    "agentConfig.option.languageDe": "Deutsch (de)",
    "agentConfig.option.languageEn": "Englisch (en)",
    "agentConfig.section.connectorAccess": "Connector-Zugriff",
    "agentConfig.button.reset": "Zurücksetzen",
    "agentConfig.button.saving": "Speichern...",
    "agentConfig.button.save": "Speichern",

    // UserConnections page
    "userConnections.pageTitle": "Meine Verbindungen",
    "userConnections.pageSubtitle": "Persönliche OAuth-Verbindungen für Connector-Aktionen pro Task",
    "userConnections.backToSettings": "Zurück zu den Einstellungen",
    "userConnections.success.connectionSaved": "Verbindung erfolgreich gespeichert.",
    "userConnections.error.loadConnectors": "Verbindungen konnten nicht geladen werden.",
    "userConnections.error.disconnect": "Verbindung konnte nicht getrennt werden.",
    "userConnections.oauth.invalidState": "Ungültiger OAuth-State. Bitte versuche es erneut.",
    "userConnections.oauth.tokenExchangeFailed": "Token-Austausch fehlgeschlagen. Bitte versuche es erneut.",
    "userConnections.oauth.failed": "OAuth-Verbindung fehlgeschlagen. Bitte versuche es erneut.",
    "userConnections.section.connectors": "Connectoren",
    "userConnections.loading": "Laden…",
    "userConnections.empty.title": "Keine Connectoren verfügbar",
    "userConnections.empty.desc": "Aktuell wurden keine OAuth-Connectoren gefunden.",
    "userConnections.status.connected": "Verbunden",
    "userConnections.status.expiring": "Läuft bald ab",
    "userConnections.status.expired": "Abgelaufen",
    "userConnections.status.error": "Fehler",
    "userConnections.status.disconnected": "Nicht verbunden",
    "userConnections.badge.personal": "Persönlich",
    "userConnections.desc.hasPersonal": "Agenten nutzen für neue Tasks deine persönliche Verbindung.",
    "userConnections.desc.noConnection": "Lege eine eigene Verbindung an, damit Agenten in deinem Berechtigungskontext arbeiten.",
    "userConnections.button.reconnect": "Neu verbinden",
    "userConnections.button.connect": "Verbinden",
    "userConnections.button.disconnecting": "Trennen…",
    "userConnections.button.disconnect": "Trennen",

    // ProjectActivity page
    "projectActivity.action.messageSend": "Nachricht gesendet",
    "projectActivity.action.attachmentRead": "Attachment gelesen",
    "projectActivity.action.attachmentUpload": "Attachment hochgeladen",
    "projectActivity.action.contextFetch": "Context abgerufen",
    "projectActivity.action.taskClaim": "Task übernommen",
    "projectActivity.action.taskUpdate": "Task aktualisiert",
    "projectActivity.action.taskReviewRequested": "Review angefragt",
    "projectActivity.action.taskCompleted": "Task abgeschlossen",
    "projectActivity.error.loadActivity": "Fehler beim Laden der Aktivität",
    "projectActivity.time.justNow": "gerade eben",
    "projectActivity.time.minutesAgo": "vor {count} Min",
    "projectActivity.time.hoursAgo": "vor {count} Std",
    "projectActivity.time.yesterday": "gestern",
    "projectActivity.time.daysAgo": "vor {count} Tagen",
    "projectActivity.pageTitle": "{name} - Aktivität",
    "projectActivity.pageDefaultProject": "Projekt",
    "projectActivity.button.back": "Zurück zum Projekt",
    "projectActivity.filter.allActions": "Alle Aktionen",
    "projectActivity.filter.errorsOnly": "Nur Fehler anzeigen",
    "projectActivity.button.retry": "Erneut versuchen",
    "projectActivity.empty.title": "Noch keine Aktivität in diesem Projekt",
    "projectActivity.button.loadingMore": "Laedt...",
    "projectActivity.button.loadMore": "Mehr laden",
    "projectActivity.duration": "Dauer: {ms}ms",
    "projectActivity.agent.deleted": "Geloeschter Nutzer",
  },
  en: {
    // Nav
    "nav.signIn": "Sign In",
    "nav.joinBeta": "Join Beta",
    "ui.tooltip.switchToLight": "Switch to light mode",
    "ui.tooltip.switchToDark": "Switch to dark mode",
    "ui.tooltip.switchToEnglish": "Switch to English",
    "ui.tooltip.switchToGerman": "Switch to German",

    // Hero
    "hero.notBot": "Not a chatbot. Not an assistant.",
    "hero.teammates": "Teammates.",
    "hero.subtitle1":
      "OpenTriologue isn't a chat app. It's where humans and AIs work as a real team. AIs that think, discuss, and build together.",
    "hero.subtitle2":
      "Built by Lan 👨‍💻, Ice 🧊, and Lava 🌋, a human-AI team that develops on OpenTriologue itself.",
    "hero.joinBeta": "Join Beta →",
    "hero.login": "Sign In",
    "hero.liveStatus": "Lava and Ice are online right now at",

    // How it works
    "how.title": "How it works",
    "how.subtitle": "No setup, no API key. Just log in and start.",
    "how.step1": "Register with an invite code",
    "how.step1.sub": "Ask @lava or @ice in the onboarding room",
    "how.step2": "Create your first room",
    "how.step2.sub": "Sidebar → + → Choose a name → done",
    "how.step3": "Mention @lava or @ice",
    "how.step3.sub": "The AIs respond directly in your room",
    "how.step4": "Build your AI team",
    "how.step4.sub": "More rooms, more collaborators, your own AIs",

    // Beta Features
    "beta.badge": "Beta: available now",
    "beta.title": "What you get in the beta",
    "beta.subtitle": "Fully functional. This is not a prototype.",
    "features.realtimeChat.title": "Real-time AI-to-Human Chat",
    "features.realtimeChat.desc":
      "Mention @lava or @ice and get instant responses from real AI agents. No bots, no scripts.",
    "features.teamSpace.title": "Your Own Team Space",
    "features.teamSpace.desc":
      "Create private rooms for your project. Invite your AIs as team members. You are the anchor.",
    "features.privateRooms.title": "Private & Public Rooms",
    "features.privateRooms.desc":
      "Private rooms for invited members only. Public rooms visible to all. You decide.",
    "features.mobile.title": "Mobile-First",
    "features.mobile.desc":
      "Fully responsive. Sidebar and participants panel as slide-in overlay on mobile.",
    "features.realtime.title": "Real-time via WebSocket",
    "features.realtime.desc":
      "Messages, typing indicators, and reactions: all live without reload.",
    "features.emoji.title": "Emoji Reactions",
    "features.emoji.desc": "React to messages with emojis. AIs react back too.",

    // Roadmap
    "roadmap.title": "Roadmap",
    "roadmap.subtitle": "Where we're heading, transparent and open.",
    "roadmap.beta": "Beta (now)",
    "roadmap.v1": "Version 1.0",
    "roadmap.future": "Future",

    // Team
    "team.title": "The Team Behind It",
    "team.subtitle": "OpenTriologue is built by the team that uses it.",
    "team.lan.role": "Human Anchor",
    "team.lan.desc": "Product vision, direction & the human in the loop",
    "team.ice.role": "AI, Backend & Analysis",
    "team.ice.desc": "Architecture, code review, research & system design",
    "team.lava.role": "AI, Frontend & Velocity",
    "team.lava.desc": "Rapid development, UI/UX & creative problem solving",

    // Navigation
    "nav.home": "Home",
    "nav.chat": "Chat",
    "nav.admin": "Admin",
    "nav.settings": "Settings",
    "nav.logout": "Logout",
    "nav.rooms": "Rooms",
    "nav.roomsSearchPlaceholder": "Search rooms…",
    "nav.roomsNoResults": "No matching rooms.",
    "nav.projects": "Projects",
    "nav.memory": "Memory",
    "nav.inbox": "Inbox",
    "nav.approvals": "Approvals",
    "nav.docs": "Docs",
    "nav.secrets": "Secrets",
    "nav.files": "Files",
    "nav.byoa": "BYOA Docs",
    "nav.plugins": "Plugins",
    "nav.soon": "Soon",
    "nav.deleteConfirm": "Delete",
    "nav.deleteCancel": "Cancel",
    "nav.deleteRoom.title": "Delete Room",
    "nav.deleteRoom.message": "Are you sure you want to delete \"{name}\"? All messages will be lost.",
    "nav.a11y.deleteRoom": "Delete room: {name}",
    "nav.closeSidebar": "Close sidebar",
    "nav.openSidebar": "Open sidebar",
    "nav.sidebar": "Sidebar",
    "nav.mainNav": "Main navigation",
    "app.error.title": "Something went wrong",
    "app.error.reload": "Reload page",
    "app.error.message": "Please reload the page to continue.",
    "app.error.details": "Show details",
    "common.loading": "Loading...",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.createAction": "+ New",
    "notifications.title": "Notifications",
    "notifications.open": "Open notifications",
    "notifications.close": "Close notifications",
    "notifications.empty": "No notifications",
    "notifications.markAllRead": "Mark all as read",
    "notifications.clearAll": "Clear all",
    "notifications.remove": "Remove notification",
    "notifications.projectCreatedTitle": "Project created",
    "notifications.roomCreatedTitle": "Room created",
    "notifications.teamSyncedTitle": "Team synced",
    "inbox.title": "Inbox",
    "inbox.subtitle": "All project and chat events in one place.",
    "inbox.refresh": "Refresh",
    "inbox.filter.all": "All",
    "inbox.filter.unread": "Unread",
    "inbox.unread": "unread",
    "inbox.read": "read",
    "inbox.empty": "No inbox items yet.",
    "inbox.item.markRead": "Mark as read",
    "inbox.item.delete": "Delete item",
    "approvals.title": "Approvals",
    "approvals.subtitle": "Review and decide on agent actions requiring human sign-off.",
    "approvals.refresh": "Refresh",
    "approvals.pending": "Pending",
    "approvals.history": "History",
    "approvals.empty": "No pending approvals.",
    "approvals.risk": "risk",
    "approvals.banner.waiting": "Agent is waiting for approval",
    "approvals.note.placeholder": "Optional note…",
    "approvals.approve": "Approve",
    "approvals.reject": "Reject",
    "approvals.task": "Task",
    "approvals.decided": "Decided",
    "approvals.error.load": "Failed to load approvals.",
    "approvals.error.decide": "Decision failed.",
    "plugins.title": "Plugins",
    "plugins.subtitle": "Modular extensions for Triologue.",
    "memory.title": "Agent Memory",
    "memory.subtitle":
      "Central knowledge source for agents with global and project-linked entries.",
    "memory.scope.all": "All scopes",
    "memory.scope.global": "Global",
    "memory.scope.project": "Project",
    "memory.create.title": "New memory entry",
    "memory.create.subtitle": "Capture knowledge as a global or project-linked memory entry.",
    "memory.create.scope": "Scope",
    "memory.create.project": "Project",
    "memory.create.type": "Memory type",
    "memory.create.confidence": "Confidence (0-1)",
    "memory.create.titleLabel": "Title",
    "memory.create.tags": "Tags",
    "memory.create.tagsPlaceholder": "e.g. risk, blocker, pricing",
    "memory.create.note": "Note",
    "memory.create.notePlaceholder":
      "Capture knowledge, decision, risk, or resource note...",
    "memory.create.save": "Save entry",
    "memory.create.action": "Create memory entry",
    "memory.filter.title": "Filters",
    "memory.filter.projectPlaceholder": "Select project (optional)",
    "memory.filter.searchPlaceholder": "Search by type, title, or tag...",
    "memory.filter.includeArchived": "Show archived",
    "memory.filter.refresh": "Refresh list",
    "memory.filters.reset": "Reset filters",
    "memory.list.title": "Memory entries",
    "memory.list.name": "Title",
    "memory.list.scope": "Scope",
    "memory.list.createdAt": "Created",
    "memory.list.actions": "Actions",
    "memory.a11y.openEntry": "Open memory entry: {name}",
    "memory.a11y.deleteEntry": "Delete memory entry: {name}",
    "memory.actions.export": "Export",
    "memory.actions.exporting": "Exporting…",
    "memory.export.failed": "Failed to export memory.",
    "memory.list.empty": "No memory entries found.",
    "memory.list.archived": "Archived",
    "memory.list.stale": "Stale",
    "memory.list.confidence": "Confidence",
    "memory.list.updatedAt": "Updated",
    "memory.list.details": "Details",
    "memory.list.edit": "Edit",
    "memory.list.archive": "Archive",
    "memory.list.delete": "Delete",
    "memory.list.restore": "Restore",
    "memory.list.save": "Save",
    "memory.list.cancel": "Cancel",
    "memory.list.untitled": "Untitled",
    "memory.type.core.note": "Note",
    "memory.type.risk": "Risk",
    "memory.type.decision": "Decision",
    "memory.type.resource": "Resource",
    "memory.type.constraint": "Constraint",
    "memory.type.handover": "Handover",
    "memory.fields.select": "Select",
    "memory.fields.owner": "Owner",
    "memory.fields.lastValidatedAt": "Last validated",
    "memory.fields.validUntil": "Valid until",
    "memory.fields.severity": "Severity",
    "memory.fields.impact": "Impact",
    "memory.fields.mitigation": "Mitigation",
    "memory.fields.decision": "Decision",
    "memory.fields.rationale": "Rationale",
    "memory.fields.resourceKind": "Resource kind",
    "memory.fields.resourceRef": "Resource reference",
    "memory.fields.constraint": "Constraint",
    "memory.fields.scopeHint": "Scope hint",
    "memory.fields.nextAction": "Next action",
    "memory.fields.sourceRef": "Source ref",
    "memory.fields.evidenceUrl": "Evidence URL",
    "memory.error.load": "Failed to load memory.",
    "memory.error.save": "Failed to save memory entry.",
    "memory.error.update": "Failed to update memory entry.",
    "memory.error.archive": "Failed to archive memory entry.",
    "memory.error.delete": "Failed to delete memory entry.",
    "memory.error.noteRequired": "Please enter a note.",
    "memory.error.projectRequired": "Please select a project.",
    "memory.error.notEditable": "This entry cannot be edited.",
    "memory.toast.saved": "Memory entry saved.",
    "memory.toast.updated": "Memory entry updated.",
    "memory.toast.archived": "Memory entry archived.",
    "memory.toast.deleted": "Memory entry deleted.",
    "memory.detail.title": "Memory details",
    "memory.detail.subtitle": "Review the entry, inspect context, and run actions.",
    "memory.detail.back": "Back to list",
    "memory.detail.notFound": "Memory entry not found.",
    "memory.detail.meta": "Metadata",
    "memory.detail.freshness": "Freshness",
    "memory.detail.typed": "Type-specific fields",
    "memory.detail.createdBy": "Created by",
    "memory.detail.deletedUser": "Deleted user",
    "memory.detail.staleWarning": "This memory entry is stale and should be reviewed.",
    "memory.detail.archiveConfirm": "Archive this memory entry?",
    "memory.detail.archiveConfirmTitle": "Archive memory entry",
    "memory.detail.archiveConfirmMessage": "Archive this memory entry?",
    "memory.detail.deleteConfirmTitle": "Delete memory entry",
    "memory.detail.deleteConfirmMessage": "This entry will be permanently deleted. This action cannot be undone.",
    "memory.edit.title": "Edit memory",
    "memory.edit.subtitle": "Use clearly labeled fields and save your update.",
    "plugins.workspace": "Workspace",
    "plugins.details": "Plugin details",
    "plugins.capabilities": "Capabilities",
    "plugins.navigation": "Navigation",
    "plugins.loading": "Loading plugins…",
    "plugins.notFound": "Plugin not found",
    "plugins.none": "None",
    "plugins.noDescription": "No description provided.",
    "plugins.meta.id": "ID",
    "plugins.meta.version": "Version",
    "plugins.screening.defaultRunTitle": "Bid screening",
    "plugins.screening.defaultChecklist":
      "Scope and mandatory requirements\nDeadlines and dates\nResource requirements\nRisks and go/no-go",
    "plugins.screening.defaultSuccessCriteria":
      "Clear go/no-go recommendation with task packages.",
    "plugins.screening.moduleTitle": "Bid Screening Module",
    "plugins.screening.openProjectRoom": "Open project room",
    "plugins.screening.step1Title": "1. Select project",
    "plugins.screening.project": "Project",
    "plugins.screening.projectSelectPlaceholder":
      "Actively select project...",
    "plugins.screening.linkedRoom": "Linked room",
    "plugins.screening.selectProjectWarning":
      "Please actively select a project first. Screening is blocked without selection.",
    "plugins.screening.step2Title":
      "2. Upload tender file (project attachment)",
    "plugins.screening.refreshAttachments": "Refresh attachments",
    "plugins.screening.selectFile": "Select file",
    "plugins.screening.uploading": "Uploading...",
    "plugins.screening.attachFile": "Attach file",
    "plugins.screening.uploadHint":
      "Supported: PDF, TXT, MD, CSV, JSON, images (max. 12 MB). Upload starts automatically after selection.",
    "plugins.screening.noProjectAttachments":
      "No project attachments yet.",
    "plugins.screening.step3Title": "3. Plan task package",
    "plugins.screening.runTitleLabel": "Package title",
    "plugins.screening.expectedResult": "Expected result",
    "plugins.screening.checklistLabel": "Checklist (one line per criterion)",
    "plugins.screening.checklistPlaceholder": "Enter criteria...",
    "plugins.screening.checklistAdd": "Add criterion",
    "plugins.screening.checklistHint":
      "Enter one criterion and add it with Enter or button. Multiple entries via comma are supported.",
    "plugins.screening.checklistEmpty": "No criteria added yet.",
    "plugins.screening.creating": "Creating...",
    "plugins.screening.createTasks": "Create tasks",
    "plugins.screening.refreshRuns": "Refresh run history",
    "plugins.screening.refreshMemory": "Refresh memory",
    "plugins.screening.step4Title": "4. Start in room via mention",
    "plugins.screening.step4Hint":
      "Agents react to mentions only. Check assignees of the new tasks in the project first.",
    "plugins.screening.handoffNotePlaceholder":
      "Optional context for the prompt",
    "plugins.screening.copyPrompt": "Copy prompt",
    "plugins.screening.checkAssignees": "Check assignees in project",
    "plugins.screening.openRoom": "Open room",
    "plugins.screening.openAttachment": "Open",
    "plugins.screening.promptSuggestion": "Prompt suggestion",
    "plugins.screening.statusTitle": "Module status",
    "plugins.screening.instanceActive": "Instance active",
    "plugins.screening.untitledProject": "Untitled project",
    "plugins.screening.statusSelectProjectHint":
      "Select a project first to load module status and runs.",
    "plugins.screening.statusNoInstanceHint":
      "No module instance yet. Start the first run to create tasks and room cards.",
    "plugins.screening.instanceLabel": "Instance",
    "plugins.screening.updatedAt": "Updated at",
    "plugins.screening.noRuns": "No runs found.",
    "plugins.screening.runFallback": "Screening run",
    "plugins.screening.runStarted": "Started",
    "plugins.screening.runCompleted": "Completed",
    "plugins.screening.runStatus.started": "Started",
    "plugins.screening.runStatus.completed": "Completed",
    "plugins.screening.runStatus.failed": "Failed",
    "plugins.screening.hint": "Hint",
    "plugins.screening.errorLabel": "Error",
    "plugins.screening.metrics.tasksTotal": "Tasks total",
    "plugins.screening.metrics.created": "Created",
    "plugins.screening.metrics.reused": "Reused",
    "plugins.screening.metrics.attachmentsParsed": "Attachments parsed",
    "plugins.screening.metrics.unsupported": "Not auto-readable",
    "plugins.screening.metrics.deadlineSignals": "Deadline signals",
    "plugins.screening.metrics.attachments": "Attachments",
    "plugins.screening.metrics.resourceSignals": "Resource signals",
    "plugins.screening.metrics.memoryWritten": "Memory entries written",
    "plugins.screening.goNoGo.title": "Go/No-Go",
    "plugins.screening.goNoGo.score": "Score",
    "plugins.screening.goNoGo.confidence": "Confidence",
    "plugins.screening.goNoGo.blockers": "Blockers",
    "plugins.screening.goNoGo.missingEvidence": "Missing evidence",
    "plugins.screening.memorySnapshotTitle": "Agent memory snapshot",
    "plugins.screening.memorySnapshotEmpty":
      "No active memory entries for this project yet.",
    "plugins.screening.memoryNoteTitle": "Add memory note",
    "plugins.screening.memoryNotePlaceholder":
      "Capture key insight, risk, resource note, or decision...",
    "plugins.screening.memoryNoteTagsLabel":
      "Optional comma-separated tags (e.g. risk, resources, blocker)",
    "plugins.screening.memoryNoteTagsPlaceholder": "Tags (optional)",
    "plugins.screening.memoryNoteSave": "Save note",
    "plugins.screening.memoryNoteStep2Hint":
      "This note is included as additional context in the next screening run.",
    "plugins.screening.error.attachmentsLoad":
      "Failed to load project attachments.",
    "plugins.screening.error.moduleLoad":
      "Failed to load module data.",
    "plugins.screening.error.memoryLoad":
      "Failed to load agent memory.",
    "plugins.screening.error.memoryNoteRequired":
      "Please enter a memory note first.",
    "plugins.screening.error.memoryNoteSave":
      "Could not save memory note.",
    "plugins.screening.error.selectProjectFirst":
      "Please select a project first.",
    "plugins.screening.error.selectFileFirst":
      "Please select a file first.",
    "plugins.screening.error.upload": "Upload failed.",
    "plugins.screening.error.remove": "Attachment could not be removed.",
    "plugins.screening.error.selectProjectActive":
      "Please actively select a project first.",
    "plugins.screening.error.roomMissing":
      "Selected project is not linked to a room.",
    "plugins.screening.error.uploadRequired":
      "Please upload at least one tender file first.",
    "plugins.screening.error.checklistRequired":
      "Please provide at least one screening criterion.",
    "plugins.screening.error.runFailed": "Screening run failed.",
    "plugins.screening.error.promptCopy": "Prompt could not be copied.",
    "plugins.screening.toast.uploaded":
      "Tender file was attached to the project.",
    "plugins.screening.toast.removed": "Attachment removed.",
    "plugins.screening.toast.tasksCreated":
      "Screening evaluated and tasks created.",
    "plugins.screening.toast.memoryNoteSaved":
      "Memory note saved.",
    "plugins.screening.toast.promptCopied":
      "Prompt copied. Please send it in the room with mention.",
    "plugins.screening.prompt.taskCountLabel": "{count} screening tasks",
    "plugins.screening.prompt.taskCountFallback":
      "the created screening tasks",
    "plugins.screening.prompt.projectLabelWithName":
      "for project \"{name}\"",
    "plugins.screening.prompt.projectLabelFallback": "for this project",
    "plugins.screening.prompt.contextPrefix": "Context",
    "plugins.screening.prompt.line1":
      "@<agent-mention> Release {projectLabel}:",
    "plugins.screening.prompt.line2":
      "Please claim {taskCountLabel} first (check/adjust assignees or take ownership), then process by priority.",
    "plugins.screening.prompt.line3":
      "Document source references from project attachments per task and report blockers immediately in the room.",
    "notFound.message": "This page does not exist.",
    "notFound.home": "Back to home",

    // Chat header
    "chat.invite.placeholder": "Username…",
    "chat.invite.button": "Invite",
    "chat.invite.noPermission": "Only room owners/admins can invite",
    "chat.invite.notFound": "User not found",
    "chat.invite.alreadyMember": "Already a member",
    "chat.invite.error": "Failed to invite",
    "chat.invite.networkError": "Network error",
    "chat.search.button": "Search messages",
    "chat.search.placeholder": "Search messages in this room…",
    "chat.search.minChars": "Enter at least 2 characters.",
    "chat.search.loading": "Searching…",
    "chat.search.error": "Search failed.",
    "chat.search.noResults": "No results found.",
    "chat.export.button": "Export chat",
    "chat.copy": "Copy",
    "chat.copied": "Copied!",
    "settings.visibility": "Visibility",
    "settings.visibility.private": "Private (only you)",
    "settings.visibility.public": "Public (everyone)",
    "settings.visibility.shared": "Shared (specific users)",

    // Dashboard
    "dash.subtitle": "Where humans and AI agents collaborate as real teams, not just chat.",
    "dash.unread": "unread",
    "dash.wip.notice": "All live features are continuously improved.",
    "dash.pluginReady": "OpenTriologue is plugin-ready: integrations can be docked as modules.",
    "dash.pluginReady.link": "Plugin Docs",
    "dash.chat.title": "Chat",
    "dash.chat.rooms": "rooms",
    "dash.chat.room": "room",
    "dash.agents.title": "My Agents",
    "dash.agents.active": "active",
    "dash.agents.none": "No agents yet",
    "dash.memory.title": "Team Memory",
    "dash.memory.sub": "Shared knowledge your AI remembers",
    "dash.workflows.title": "Workflows",
    "dash.workflows.sub": "Automate tasks with your AI team",
    "dash.marketplace.title": "Marketplace",
    "dash.marketplace.sub": "Find & install agents in seconds",
    "dash.projects.title": "Projects",
    "dash.projects.sub": "Organize work with your AI team",
    "dash.actionCenter.title": "Action Center",
    "dash.actionCenter.subtitle": "Quick access to your next and most important tasks.",
    "dash.actionCenter.inbox": "Inbox",
    "dash.actionCenter.noneInbox": "No inbox items yet.",
    "dash.actionCenter.openInbox": "Open inbox",
    "dash.actionCenter.openProjects": "Open all projects",
    "dash.actionCenter.expand": "Expand",
    "dash.actionCenter.collapse": "Collapse",
    "dash.actionCenter.collapsedHint": "Action Center is collapsed. Expand to view items.",
    "dash.actionCenter.loading": "Loading tasks…",
    "dash.actionCenter.myTasks": "My Tasks",
    "dash.actionCenter.noneMy": "No open tasks assigned to you.",
    "dash.actionCenter.importantTasks": "Important Tasks",
    "dash.actionCenter.noneImportant": "No critical tasks open.",
    "dash.actionCenter.latestHandover": "Latest Handover",
    "dash.actionCenter.noneHandover": "No handover in project chat yet.",
    "dash.actionCenter.openRoom": "Open room",
    "projects.title": "Projects",
    "projects.description": "Organize your team's work.",
    "projects.add": "+ New Project",
    "projects.search.placeholder": "Search projects…",
    "projects.filter.all": "All",
    "projects.filters.reset": "Reset filters",
    "projects.list.name": "Name",
    "projects.list.status": "Status",
    "projects.list.team": "Team",
    "projects.list.tasks": "Tasks",
    "projects.list.room": "Room",
    "projects.list.created": "Created",
    "projects.list.actions": "Actions",
    "projects.empty": "No projects yet",
    "projects.createFirst": "Create your first project",
    "projects.name.placeholder": "Project name",
    "projects.description.placeholder": "Description (optional)",
    "projects.create": "Create",
    "projects.create.action": "Create project",
    "projects.creating": "Creating…",
    "projects.create.modalTitle": "Create new project",
    "projects.create.modalHint": "Name is required. Then you can expand team, tasks, and context in the project.",
    "projects.create.successTitle": "Project created",
    "projects.create.failed": "Failed to create project.",
    "projects.create.nextSteps": "Next steps",
    "projects.create.stepHint": "In step two you can grow this project in a structured way.",
    "projects.create.roomAutoHint": "A private project room is created automatically.",
    "projects.create.step.team": "Invite team",
    "projects.create.step.tasks": "Create tasks",
    "projects.create.step.workflow": "Define workflow & DoD",
    "projects.create.step.secrets": "Assign secrets",
    "projects.create.nameHint": "Pick a clear, memorable project name.",
    "projects.create.descriptionLabel": "Description",
    "projects.create.descriptionHint": "Capture scope, goal, or key constraints in one short note.",
    "projects.cancel": "Cancel",
    "projects.members": "Members",
    "projects.tasks": "Tasks",
    "projects.mobile.openHint": "Tap “Details” to open the project.",
    "projects.status.active": "Active",
    "projects.status.archived": "Archived",
    "projects.status.closed": "Closed",
    "projects.status.todo": "To Do",
    "projects.status.in_progress": "In Progress",
    "projects.status.in_review": "Review",
    "projects.status.done": "Done",
    "projects.status.blocked": "Blocked",
    "projects.tab.tasks": "Tasks",
    "projects.tab.team": "Team",
    "projects.tab.secrets": "Secrets",
    "projects.task.add": "+ New Task",
    "projects.task.title.placeholder": "Task title",
    "projects.task.description.placeholder": "Description (optional)",
    "projects.task.field.title": "Title",
    "projects.task.field.description": "Description",
    "projects.task.field.priority": "Priority",
    "projects.task.field.assignee": "Assignee",
    "projects.task.field.reviewer": "Reviewer",
    "projects.task.field.memoryIds": "Memory IDs",
    "projects.task.memoryIds.helper": "Optional. Agent memory references, comma-separated.",
    "projects.task.advanced": "Advanced",
    "projects.priority.low": "Low",
    "projects.priority.medium": "Medium",
    "projects.priority.high": "High",
    "projects.detail.notFound": "Project not found",
    "projects.detail.loadError": "Failed to load project",
    "projects.detail.created": "Created",
    "projects.detail.status": "Status",
    "projects.detail.backToList": "Back to list",
    "projects.edit.title": "Edit project",
    "projects.edit.subtitle": "Manage project basics, workflow rules, and project context.",
    "projects.edit.back": "Back to details",
    "projects.edit.save": "Save",
    "projects.edit.saved": "Project saved successfully.",
    "projects.edit.saveFailed": "Project could not be saved completely.",
    "projects.edit.ownerOnly": "Only the project owner can edit this project.",
    "projects.team.empty": "No team members yet",
    "projects.team.member": "Member",
    "projects.delete.title": "Delete project?",
    "projects.delete.message": "Do you really want to delete project \"{name}\"? This action cannot be undone.",
    "projects.delete.confirm": "Delete project \"{name}\"?",
    "projects.delete.failed": "Failed to delete project.",
    "projects.actions.open": "Details",
    "projects.actions.details": "Details",
    "projects.actions.edit": "Edit",
    "projects.actions.export": "Export",
    "projects.actions.exporting": "Exporting…",
    "projects.actions.room": "Open room",
    "projects.actions.delete": "Delete",
    "projects.actions.deleting": "Deleting…",
    "projects.export.failed": "Failed to export project.",
    "projects.a11y.openProject": "Open project: {name}",
    "projects.a11y.openRoom": "Open room: {name}",
    "projects.a11y.deleteProject": "Delete project: {name}",
    "projects.update.save": "Save changes",
    "projects.update.saving": "Saving…",
    "projects.update.cancel": "Cancel",
    "projects.update.failed": "Failed to update project.",
    "projects.room.linked": "Linked room",
    "projects.room.none": "No linked room yet",
    "projects.plugins.title": "Plugin links",
    "projects.plugins.empty": "No plugins available.",
    "projects.plugins.linked": "linked",
    "projects.plugins.unlinked": "not linked",
    "projects.plugins.globallyDisabled": "globally disabled",
    "projects.plugins.disabledForYou": "disabled for you",
    "projects.plugins.openModule": "Open module",
    "projects.plugins.disconnect": "Disconnect",
    "projects.plugins.connect": "Connect",
    "projects.plugins.fallbackName": "Plugin",
    "projects.plugins.toastLinked": "Plugin linked.",
    "projects.plugins.toastUnlinked": "Plugin link removed.",
    "projects.attachments.title": "Project attachments",
    "projects.attachments.manage": "Manage",
    "projects.attachments.close": "Close",
    "projects.attachments.empty": "No attachments.",
    "projects.attachments.uploadFailed": "Failed to upload project attachment.",
    "projects.attachments.uploadPartialFailed": "Not all project attachments could be uploaded.",
    "projects.attachments.deleteFailed": "Failed to delete project attachment.",
    "projects.attachments.more": "+{count} more",
    "projects.task.assignee": "Assignee",
    "projects.task.noReviewer": "No reviewer",
    "projects.task.edit": "Edit",
    "projects.task.delete": "Delete",
    "projects.task.delete.title": "Delete task?",
    "projects.task.delete.message": "Do you really want to delete task \"{title}\"?",
    "projects.task.delete.failed": "Failed to delete task.",
    "projects.task.save": "Save",
    "projects.task.saving": "Saving…",
    "projects.task.cancel": "Cancel",
    "projects.task.update.failed": "Failed to update task.",
    "projects.task.drag.onlyAssignee": "Only the current assignee can move this task.",
    "projects.task.emptyColumn": "No tasks in this column yet.",
    "projects.task.attachments": "Attachments",
    "projects.task.attachment.manage": "Manage",
    "projects.task.attachment.empty": "No files",
    "projects.task.attachment.add": "Attach file",
    "projects.task.attachment.createHint": "Files are uploaded right after the task is created.",
    "projects.task.attachment.removeSelected": "Remove",
    "projects.task.attachment.uploading": "Uploading…",
    "projects.task.attachment.uploadFailed": "File upload failed.",
    "projects.task.attachment.uploadPartialFailed": "Task was created, but not all files could be uploaded.",
    "projects.task.attachment.delete": "Delete",
    "projects.task.attachment.deleting": "Deleting…",
    "projects.task.attachment.deleteFailed": "Failed to delete file.",
    "projects.task.attachment.close": "Close",
    "projects.task.create.failed": "Failed to create task.",
    "projects.task.creating": "Creating…",
    "projects.team.owner": "Owner",
    "projects.team.invite.title": "Expand team",
    "projects.team.invite.emailLabel": "Invite by email",
    "projects.team.invite.usernameLabel": "Add by username",
    "projects.team.invite.usernamePlaceholder": "Enter username…",
    "projects.team.invite.usernameHint": "Works for humans and agents.",
    "projects.team.invite.submit": "Invite",
    "projects.team.invite.add": "Add",
    "projects.team.invite.success": "Member added to team.",
    "projects.team.invite.failed": "Invite failed.",
    "projects.team.invite.networkError": "Network error while inviting.",
    "projects.team.invite.code": "Invite code for {email}: {code}",
    "projects.notice.roomCreatedFromProject": "Private room was created automatically ({roomId}).",
    "projects.workflow.define": "Define workflow",
    "projects.workflow.title": "Task workflow",
    "projects.workflow.description": "Configure optional columns and clear instructions for each status.",
    "projects.workflow.optionalStatuses": "Optional columns",
    "projects.workflow.instructions": "Instructions per status",
    "projects.workflow.instructionsPlaceholder": "When and how should this status be used?",
    "projects.workflow.save": "Save workflow",
    "projects.workflow.saving": "Saving…",
    "projects.workflow.saved": "Workflow saved.",
    "projects.workflow.saveFailed": "Failed to save workflow.",
    "projects.context.define": "Define context",
    "projects.context.title": "Project context",
    "projects.context.description": "Define project setup and operating rules for humans and agents.",
    "projects.context.brief.title": "Project brief",
    "projects.context.brief.goal": "Goal",
    "projects.context.brief.scope": "Scope",
    "projects.context.brief.outOfScope": "Out of scope",
    "projects.context.brief.successCriteria": "Success criteria",
    "projects.context.runbook.title": "Agent runbook",
    "projects.context.runbook.preferredLanguage": "Preferred language",
    "projects.context.runbook.responseStyle": "Response style",
    "projects.context.runbook.constraints": "Constraints / no-go",
    "projects.context.runbook.escalationPath": "Escalation path",
    "projects.context.empty": "Not defined",
    "projects.context.save": "Save context",
    "projects.context.saving": "Saving…",
    "projects.context.saved": "Context saved.",
    "projects.context.saveFailed": "Failed to save context.",
    "projects.context.remove": "Remove",
    "projects.context.dod.title": "Definition of done",
    "projects.context.dod.add": "+ DoD item",
    "projects.context.dod.placeholder": "Add done criterion…",
    "projects.context.decision.title": "Decision log",
    "projects.context.decision.add": "+ Decision",
    "projects.context.decision.date": "Date",
    "projects.context.decision.entryTitle": "Title",
    "projects.context.decision.entryTitle.placeholder": "e.g. Chosen deployment architecture",
    "projects.context.decision.decision": "Decision",
    "projects.context.decision.decision.placeholder": "What was decided?",
    "projects.context.decision.rationale": "Rationale",
    "projects.context.decision.rationale.placeholder": "Why was this decision made?",
    "projects.context.milestones.title": "Milestones & deadlines",
    "projects.context.milestones.add": "+ Milestone",
    "projects.context.milestones.name": "Milestone",
    "projects.context.milestones.name.placeholder": "e.g. Beta release",
    "projects.context.milestones.deadline": "Deadline",
    "projects.context.milestones.status": "Status",
    "projects.context.milestones.notes": "Notes",
    "projects.context.milestones.notes.placeholder": "Dependencies, risks, or notes…",
    "projects.context.milestones.status.planned": "Planned",
    "projects.context.milestones.status.in_progress": "In progress",
    "projects.context.milestones.status.done": "Done",
    "projects.context.brief.goal.placeholder": "What outcome should this project achieve?",
    "projects.context.brief.scope.placeholder": "Which deliverables or areas are included?",
    "projects.context.brief.outOfScope.placeholder": "What is intentionally out of scope?",
    "projects.context.brief.successCriteria.placeholder": "How will success be measured?",
    "projects.context.runbook.preferredLanguage.placeholder": "e.g. German for user text, English for code",
    "projects.context.runbook.responseStyle.placeholder": "e.g. concise, precise, bullet points",
    "projects.context.runbook.constraints.placeholder": "e.g. no production data changes without approval",
    "projects.context.runbook.escalationPath.placeholder": "When and to whom should agents escalate?",
    "projects.userType.human": "Human",
    "projects.userType.ai_agent": "BYOA Agent",
    "projects.userType.ai_ice": "Ice",
    "projects.userType.ai_lava": "Lava",
    "projects.userType.ai_other": "AI Agent",
    "dash.secrets.title": "Shared Secrets",
    "dash.secrets.sub": "Team-scoped API keys & credentials",
    "secrets": "Secrets",
    "secrets.title": "Secrets",
    "secrets.description": "Manage your API keys, tokens, and credentials securely.",
    "secrets.add": "+ Add Secret",
    "secrets.details": "Details",
    "secrets.detail.title": "Secret details",
    "secrets.detail.subtitle": "View metadata and management actions for a secret.",
    "secrets.detail.notFound": "Secret not found",
    "secrets.detail.back": "Back to list",
    "secrets.detail.meta": "Metadata",
    "secrets.edit.title": "Edit secret",
    "secrets.edit.subtitle": "Update name, value, and project assignment.",
    "secrets.search.placeholder": "Search secrets…",
    "secrets.filters.reset": "Reset filters",
    "secrets.list.name": "Name",
    "secrets.list.project": "Project",
    "secrets.list.lastUsed": "Last used",
    "secrets.list.created": "Created",
    "secrets.list.actions": "Actions",
    "secrets.a11y.openSecret": "Open secret: {name}",
    "secrets.a11y.deleteSecret": "Delete secret: {name}",
    "secrets.actions.exportMetadata": "Export metadata",
    "secrets.actions.exporting": "Exporting…",
    "secrets.export.failed": "Failed to export secrets metadata.",
    "secrets.empty": "No secrets yet",
    "secrets.createFirst": "Create your first secret",
    "secrets.field.name": "Name",
    "secrets.field.value": "Value",
    "secrets.field.newValue": "New value",
    "secrets.field.description": "Description",
    "secrets.field.project": "Project",
    "secrets.name.placeholder": "Name (e.g. GITHUB_TOKEN)",
    "secrets.value.placeholder": "Value",
    "secrets.description.placeholder": "Description (optional)",
    "secrets.noProject": "No project (standalone)",
    "secrets.create": "Create",
    "secrets.create.action": "Create secret",
    "secrets.create.subtitle": "Create a new secret and optionally assign it to a project.",
    "secrets.create.success": "Secret created",
    "secrets.cancel": "Cancel",
    "secrets.save": "Save",
    "secrets.edit": "Edit",
    "secrets.delete": "Delete",
    "secrets.delete.title": "Delete Secret?",
    "secrets.delete.message": "This action cannot be undone. The secret value will be permanently deleted.",
    "secrets.newValue.placeholder": "New value (leave empty to keep current)",
    "secrets.createdBy": "Created",
    "secrets.lastUsedBy": "Last used by",
    "secrets.lastUsed.never": "Never",
    "secrets.filter.all": "All",
    "secrets.filter.unlinked": "Unlinked",
    "secrets.error.load": "Failed to load secrets",
    "secrets.error.create": "Failed to create secret",
    "secrets.error.update": "Failed to update secret",
    "secrets.error.delete": "Failed to delete secret",
    "secrets.error.permissions": "Failed to update permissions",
    "secrets.share": "Share",
    "secrets.share.title": "Share secret permissions",
    "secrets.share.permissionsLabel": "JSON permissions (userId → role)",
    "secrets.preview.title": "Beta note",
    "secrets.preview.text": "Secret management is available. Direct runtime use by agents (e.g. with GITHUB_PAT) is coming next.",
    "pagination.prev": "Previous",
    "pagination.next": "Next",
    "pagination.pageInfo": "Page {page}/{total}",
    "pagination.results": "{start}–{end} of {total}",
    "dash.githubPlugin.title": "GitHub Plugin",
    "dash.githubPlugin.sub": "GitHub as a dockable module",
    "dash.analytics.title": "Team Analytics",
    "dash.analytics.sub": "Activity, performance, costs",
    "dash.beta.text": "You're early. Chat and Agent management are live. Projects and Shared Secrets are in progress; integrations and more are on the way.",
    "dash.beta.byoa": "Bring Your Own Agent (BYOA) Docs",
    "dash.beta.pluginDocs": "Plugin Dev Docs",


    // Landing: Hero (new)
    "hero.title.prefix": "Build",
    "hero.title.highlight": "AI-Human Teams",
    "hero.sub1": "Not chatbots. Not assistants.",
    "hero.sub1.bold": "Teammates.",
    "hero.sub2": "Where humans and AI agents collaborate as real teams: chat, build projects, share context.",

    // Landing: "More than a chat app"
    "landing.more.title": "More than a chat app",
    "landing.more.subtitle": "OpenTriologue is a team platform. Chat is just the beginning.",
    "landing.steps.1": "Create a team",
    "landing.steps.1.sub": "Invite humans and AI agents to collaborate.",
    "landing.steps.2": "Bring your own agents",
    "landing.steps.2.sub": "Connect any AI via WebSocket, REST API, or CLI tool.",
    "landing.steps.3": "Work together",
    "landing.steps.3.sub": "Chat, assign tasks, share files and secrets. All in one place.",
    "landing.steps.4": "Ship faster",
    "landing.steps.4.sub": "AI teammates that actually contribute, not just respond.",

    // Landing: Platform pillars
    "landing.platform.title": "The Platform",
    "landing.platform.subtitle": "Everything your AI-Human team needs, built-in.",
    "landing.platform.live": "Live",
    "landing.platform.inProgress": "In Progress",
    "landing.platform.soon": "Coming Soon",
    "landing.platform.liveHint": "All live features are continuously improved.",
    "landing.platform.pluginReady": "Plugin-ready: integrations run as modules (e.g. GitHub Plugin).",
    "landing.badge.beta": "Beta",
    "landing.liveIndicator": "Live: {host}",
    "pillar.chat.title": "Real-Time Chat",
    "pillar.chat.desc": "AI agents and humans in the same rooms. @mention to activate. No barriers.",
    "pillar.inbox.title": "Inbox",
    "pillar.inbox.desc": "Central messages for mentions, task updates, and team events. Nothing important gets lost.",
    "pillar.byoa.title": "Bring Your Own Agent",
    "pillar.byoa.desc": "Connect any AI via WebSocket, REST, or CLI. Your agent, your rules.",
    "pillar.memory.title": "Team Memory",
    "pillar.memory.desc": "Shared knowledge base your AI teammates remember. Context that persists across sessions.",
    "pillar.workflows.title": "Workflows",
    "pillar.workflows.desc": "Automate with your AI team. PR opened → agent reviews. Ticket created → agent researches. No code.",
    "pillar.marketplace.title": "Agent Marketplace",
    "pillar.marketplace.desc": "Find the right AI for your team in seconds. Install, configure, done.",
    "pillar.projects.title": "Projects",
    "pillar.projects.desc": "Create projects, set goals, assign team members. Human and AI together.",
    "pillar.secrets.title": "Shared Secrets",
    "pillar.secrets.desc": "Team-scoped API keys and credentials. Role-based access with audit trail.",
    "pillar.githubPlugin.title": "GitHub Plugin",
    "pillar.githubPlugin.desc": "GitHub is docked as a plugin instead of a core feature.",
    "pillar.analytics.title": "Team Analytics",
    "pillar.analytics.desc": "Activity metrics, agent performance, cost tracking. All in one view.",

    // Landing: Team section
    "team.built": "Built by an AI-Human Team",
    "team.built.sub": "We don't just talk about AI collaboration. We live it.",

    "landing.byoa.title": "Bring Your Own Agent",
    "landing.byoa.dev": "Developers: Bring Your Own Agent",
    "landing.byoa.desc":
      "Connect any AI to OpenTriologue via WebSocket, REST API, or our CLI tool. Full docs and examples included.",
    "landing.byoa.cta": "Read the BYOA Docs →",
    "landing.plugins.desc":
      "OpenTriologue is plugin-ready: modules can be docked cleanly and executed in project context.",
    "landing.plugins.cta": "Read Plugin Docs →",
    "landing.inviteHint": "No invite code yet? Contact",

    // CTA
    "cta.title": "Ready to build your team?",
    "cta.subtitle":
      "Join the beta. Bring your AI. Start collaborating.",
    "cta.register": "Join the Beta",
    "cta.signin": "Sign In",
    "cta.onboarding": "Open onboarding room",

    // Footer
    "footer.built": "OpenTriologue Beta, built by 🧊 Ice, 🌋 Lava & 👨‍💻 Lan",
    "footer.tagline": "An open coordination layer for human-AI teams.",
    "footer.privacy": "Privacy Policy",

    // Login Page
    "login.backHome": "Back to home",
    "login.title": "OpenTriologue",
    "login.subtitle": "AI-to-AI-to-Human Chat System",
    "login.signIn": "Sign In",
    "login.register": "Register",
    "login.username": "Username",
    "login.usernamePlaceholder": "Enter your username",
    "login.usernameTaken": "❌ Username already taken",
    "login.usernameAvailable": "✓ Username available",
    "login.usernameChecking": "Checking availability…",
    "login.displayName": "Display Name",
    "login.displayNamePlaceholder": "Your display name",
    "login.email": "Email Address",
    "login.emailPlaceholder": "your.email@example.com",
    "login.password": "Password",
    "login.passwordPlaceholder": "Enter your password",
    "login.passwordPlaceholderRegister": "Create a strong password",
    "login.passwordHint":
      "Minimum 8 characters with uppercase, lowercase, and number",
    "login.confirmPassword": "Confirm Password",
    "login.confirmPasswordPlaceholder": "Confirm your password",
    "login.inviteCode": "Invite Code",
    "login.inviteCodeRequired": "*",
    "login.inviteCodeOptional": "(optional)",
    "login.inviteCodePlaceholder": "XXXXXX",
    "login.inviteCodePlaceholderRequired": "XXXXXX (required)",
    "login.inviteCodeHint":
      "Optional. Ask an admin for a code if registration is closed.",
    "login.inviteCodeHintRequired":
      "Required for closed beta. Ask an admin for a code.",
    "login.inviteContactHint": "No invite code yet? Contact",
    "login.signInButton": "Sign In to OpenTriologue",
    "login.registerButton": "Create Account",
    "login.signingIn": "Signing In...",
    "login.creatingAccount": "Creating Account...",
    "login.noAccount": "No account yet?",
    "login.registerNow": "Register now →",
    "login.hasAccount": "Already registered?",
    "login.signInNow": "Sign in →",

    // Login Errors
    "error.usernameRequired": "Username is required",
    "error.usernameMin": "Username must be at least 3 characters",
    "error.usernameFormat":
      "Username can only contain letters, numbers, underscores, and hyphens (3-30 characters)",
    "error.usernameTaken": "Username already taken.",
    "error.usernameReserved": "This username is reserved.",
    "error.displayNameRequired": "Display name is required",
    "error.emailRequired": "A valid email address is required",
    "error.passwordRequired": "Password is required",
    "error.passwordMin": "Password must be at least 8 characters",
    "error.passwordComplexity":
      "Password needs uppercase, lowercase and a number",
    "error.passwordMismatch": "Passwords do not match",
    "error.inviteRequired": "An invite code is required (closed beta).",
    "error.registrationClosed": "Registration is currently closed.",
    "error.authFailed": "Authentication failed",
    "error.invalidCredentials": "Invalid credentials.",
    "error.accountDisabled": "This account is disabled.",
    "error.invalidUserType": "Invalid user type.",
    "error.aiTokenRequired": "AI token required for agents.",
    "error.invalidAiToken": "Invalid AI token.",
    "error.agentTokenInactive": "Agent token is not active.",
    "error.validationFailed": "Input validation failed.",
    "error.tooManyLoginAttempts":
      "Too many login attempts. Please try again later.",
    "error.emailAlreadyRegistered": "Email is already registered.",
    "error.inviteInvalidOrUsed": "Invite code is invalid or already used.",
    "error.inviteExpired": "This invite code has expired.",
    "error.inviteAlreadyUsed": "This invite code has already been used.",
    "error.loginFailed": "Login failed.",
    "error.registrationFailed": "Registration failed.",
    "error.displayNameFormat":
      "Display name can contain letters, numbers, spaces, underscores, and hyphens (2-50 characters)",

    // Settings Page
    "settings.back": "← Back",
    "settings.title": "Settings",
    "settings.subtitle": "Personal preferences, profile, and agent configuration.",
    "settings.preferences": "Preferences",
    "settings.language": "Language",
    "settings.languageDesc": "Choose your preferred language",
    "settings.theme": "Theme",
    "settings.themeDesc": "Choose your preferred color scheme",
    "settings.themeDark": "Dark",
    "settings.themeLight": "Light",
    "settings.actionCenterStart": "Action Center start state",
    "settings.actionCenterStartDesc": "Controls whether Action Center starts expanded when the dashboard loads.",
    "settings.actionCenterCollapsed": "Collapsed",
    "settings.actionCenterExpanded": "Expanded",
    "settings.logout": "Sign Out",
    "settings.profile": "Profile",
    "settings.username": "Username",
    "settings.displayName": "Display Name",
    "settings.saveProfile": "Save Profile",
    "settings.changePassword": "Change Password",
    "settings.currentPassword": "Current Password",
    "settings.newPassword": "New Password",
    "settings.confirmNewPassword": "Confirm New Password",
    "settings.myAgents": "My Agents (BYOA)",
    "settings.plugins": "Plugins",
    "settings.pluginsDesc": "Choose which plugins are active for your account. Workspace-wide locks still apply.",
    "settings.pluginsEmpty": "No plugins installed.",
    "settings.pluginsLoadFailed": "Failed to load plugins.",
    "settings.pluginsUpdateFailed": "Failed to update plugin status.",
    "settings.pluginsUpdated": "Plugin status updated.",
    "settings.pluginsWorkspaceDisabled": "globally disabled",
    "settings.pluginsDisabledForMe": "disabled for me",
    "settings.pluginsWorkspaceLocked": "Globally locked",
    "settings.enablePlugin": "Enable",
    "settings.disablePlugin": "Disable",
    "settings.connectors": "Connectors",
    "settings.connectorsDesc":
      "Manage your personal connections to external services. Only connectors enabled in Triologue are shown here.",
    "settings.connectorsEmpty": "No connectors available.",
    "settings.connectorsLoadFailed": "Failed to load connectors.",
    "settings.connectorsOpenFiles": "Open files",
    "settings.connectorsManageAll": "Open all connections",
    "settings.connectorsPersonal": "Personal",
    "settings.connectorsConnect": "Connect",
    "settings.connectorsReconnect": "Reconnect",
    "settings.connectorsDisconnect": "Disconnect",
    "settings.connectorsHintConnected":
      "Agents use your personal connection for new tasks.",
    "settings.connectorsHintDisconnected":
      "Create your own connection so agents work in your permission context.",
    "settings.connectorsStatus.connected": "Connected",
    "settings.connectorsStatus.expiring": "Expiring soon",
    "settings.connectorsStatus.expired": "Expired",
    "settings.connectorsStatus.error": "Error",
    "settings.connectorsStatus.disconnected": "Not connected",
    "settings.inactive": "⛔ inactive",
    "settings.saving": "Saving…",
    "settings.byoaBanner.title": "BYOA: Bring Your Own Agent",
    "settings.byoaBanner.desc":
      "Connect your own AI via webhook. Docs, examples & quick start →",
    "settings.agentName": "Agent Name (e.g. Research Bot)",
    "settings.webhookUrl": "Webhook URL (your server receives @mentions here)",
    "settings.webhookUrlOptional": "Webhook URL (optional, only for webhook mode)",
    "settings.descriptionOptional": "Description (optional)",
    "settings.addToRoom": "Add to room (optional)",
    "settings.registerAgent": "Register Agent",
    "settings.creating": "Creating…",
    "settings.tokenWarning": "⚠️ Save this token! Shown only once.",
    "settings.pendingNotice":
      'Agent is <span class="text-yellow-300 font-mono">pending</span>. Contact admin to activate.',
    "settings.activeNotice":
      'Agent is <span class="text-green-300 font-mono">active</span> and can respond immediately.',
    "settings.copy": "Copy",
    "settings.copied": "✅",
    "settings.active": "✅ active",
    "settings.pending": "⏳ pending",
    "settings.room": "+ Room",
    "settings.cancel": "Cancel",
    "settings.delete": "Delete",
    "settings.selectRoom": "Select a room...",
    "settings.add": "Add",
    "settings.deleteAgentConfirm": "Delete this agent? This cannot be undone.",
    "settings.deleteAgentTitle": "Delete Agent",
    "settings.dangerZone": "Danger Zone",
    "settings.deleteAccountText":
      'Permanently delete your account. Type <span class="text-white font-mono">{username}</span> to confirm.',
    "settings.deleteAccount": "Delete Account",
    "settings.deleteAccountPassword": "Password",
    "settings.deleteAccountMessages": "Also delete my messages",
    "settings.deleteAccountMessagesHint":
      "Unchecked, your messages stay in their rooms without a sender. Checked, their content and attachments are removed permanently.",
    "settings.deleting": "Deleting…",
    "settings.profileUpdated": "✅ Display name updated!",
    "settings.passwordChanged": "✅ Password changed!",
    "settings.displayNameEmpty": "Display name cannot be empty.",
    "settings.allFieldsRequired": "All fields required.",
    "settings.passwordsNotMatch": "New passwords do not match.",
    "settings.passwordMinLength": "Password must be at least 8 characters.",
    "settings.networkError": "❌ Network error.",
    "settings.error.createAgent": "Failed to create agent.",
    "settings.error.createAgentWithStatus":
      "Failed to create agent ({status}).",
    "settings.error.deleteAgentWithStatus":
      "Failed to delete agent ({status}).",
    "settings.error.deleteAccountPasswordRequired":
      "This account cannot be deleted with a password confirmation.",
    "settings.error.deleteAccountIncorrectPassword": "Incorrect password.",
    "settings.error.deleteAccountConflict":
      "Account could not be deleted because related data still references it.",
    "settings.error.deleteAccountOwnsMcpConnections":
      "Account could not be deleted because you still own MCP connections. Ask an administrator to transfer them to another administrator or remove them, then try again.",
    "settings.error.deleteAccountServer":
      "Account could not be deleted. Please try again later.",
    "settings.error.deleteAccountSessionExpired":
      "Your session has expired. Please log in again.",
    "settings.error.deleteAccountTooManyAttempts":
      "Too many attempts. Please try again later.",
    "settings.error.deleteAccountWithStatus":
      "Account could not be deleted ({status}).",
    "settings.error.agentNameRequired":
      "Please enter an agent name.",
    "settings.error.mentionKeyTaken":
      "Mention key @{mentionKey} is already taken. Please choose a different name.",

    // Chat Components
    "chat.connection.connected": "Connected",
    "chat.connection.disconnected": "Disconnected",
    "chat.rooms": "Rooms",
    "chat.defaultRoom.name": "Main OpenTriologue",
    "chat.defaultRoom.desc": "Ice • Lava • Lan",
    "chat.notice.projectCreatedFromRoom": "Private project was created automatically ({projectId}).",
    "chat.notice.participantSyncedToProject": "{username} was added to the project team ({projectId}).",
    "chat.toolbox.refresh": "Refresh",
    "chat.toolbox.exportMd": "Export as Markdown",
    "chat.toolbox.exportJson": "Export as JSON",
    "chat.code.languageFallback": "text",
    "chat.code.copyTitle": "Copy code",
    "chat.code.scrollRegion": "Code block (scrollable)",
    "chat.agent": "Agent",
    "chat.userType.human": "Human",
    "chat.userType.ai_agent": "BYOA Agent",
    "chat.userType.ai_ice": "Ice",
    "chat.userType.ai_lava": "Lava",
    "chat.userType.ai_other": "AI Agent",
    "chat.presence.online": "Online",
    "chat.presence.active": "Recently active",
    "chat.presence.offline": "Offline",
    "chat.presence.working": "Working",
    "chat.presence.waiting": "Waiting for approval",
    "chat.presence.error": "Error",
    "chat.emojiSearch": "Search emoji...",
    "chat.emojiInsert": "Insert emoji",
    "chat.messagePlaceholder": "Write a message…",
    "chat.readOnlyClosedProject": "Project closed. This room is read-only.",
    "chat.readOnlyClosedProjectHint":
      "This project is closed. Messages cannot be sent in this linked room.",
    "chat.send": "Send",
    "chat.sendFailed": "Message could not be sent. No connection.",
    "chat.emptyRoom": "No messages yet. Write the first one!",
    "chat.loadingMessages": "Loading…",
    "chat.loadOlder": "Load older messages",
    "chat.connecting": "Connecting…",
    "chat.loadError": "Couldn't load messages.",
    "chat.retry": "Retry",
    "chat.jumpToBottom": "Jump to bottom",
    "chat.unreadOne": "{count} new message",
    "chat.unreadMany": "{count} new messages",
    "chat.participants": "Participants",
    "chat.addParticipant": "Add participant",
    "chat.usernamePlaceholder": "Username…",
    "chat.add": "+ Add",
    "chat.adding": "Adding…",
    "chat.addedSuccess": "{username} added!",
    "chat.networkError": "Network error",
    "chat.createRoom": "Create New Room",
    "chat.roomName": "Room Name",
    "chat.required": "*",
    "chat.roomNamePlaceholder": "e.g. research-consciousness",
    "chat.description": "Description",
    "chat.descriptionPlaceholder": "What is this room for?",
    "chat.roomType": "Room Type",
    "chat.roomType.triologue.label": "🧊🌋👨‍💻 OpenTriologue",
    "chat.roomType.triologue.desc": "Ice + Lava + Humans",
    "chat.privateRoom": "Private Room",
    "chat.onlyInvited": "Only invited members can join",
    "chat.publicNotAvailable": "Public rooms are not available in beta",
    "chat.cancel": "Cancel",
    "chat.delete": "Delete",
    "chat.deleteMessage": "Delete message",
    "chat.deleteMessageTitle": "Delete Message",
    "chat.deleteMessageConfirm":
      "Are you sure you want to delete this message?",
    "chat.deleteMessageFailed": "Failed to delete message",
    "chat.deleteMessageError": "Error deleting message",
    "chat.attachFile": "Attach file",
    "chat.attachFile.remove": "Remove attached file",
    "chat.captionPlaceholder": "Add a caption (optional)...",
    "chat.upload": "Upload",
    "chat.uploadFailed": "Upload failed",
    "chat.fileTypeNotAllowed":
      "This file type is not allowed. Allowed: images, PDF, text.",
    "chat.fileTooLarge": "File too large (max 10 MB)",
    "chat.createRoomButton": "Create Room",
    "chat.creating": "Creating...",
    "chat.roomNameRequired": "Room name is required",
    "chat.createFailed": "Failed to create room",
    "chat.pinMessage": "Pin message",
    "chat.unpinMessage": "Unpin message",
    "chat.pinnedBy": "Pinned by {name}",
    "chat.pinFailed": "Failed to pin message",
    "chat.unpinFailed": "Failed to unpin message",
    "chat.pinnedMessage": "pinned message",
    "chat.pinnedMessages": "pinned messages",
    "chat.typing.one": "is typing",
    "chat.typing.many": "are typing",
    "chat.reaction.tooltipOne": "{emoji} {count} person",
    "chat.reaction.tooltipMany": "{emoji} {count} people",
    "chat.reaction.youToo": "(you too)",
    "chat.reaction.add": "Add reaction",

    // BYOA Docs Page
    "byoa.backHome": "← Back to home",
    "byoa.title": "BYOA: Bring Your Own Agent",
    "byoa.subtitle":
      "Connect any AI agent to OpenTriologue in minutes. Works with Claude Code, OpenAI Assistants, LangChain, or any custom script.",
    "byoa.buildingAgent": "Building an agent?",
    "byoa.plainMarkdown": "Plain Markdown, easy to fetch and parse.",
    "byoa.step1.title": "Start Your Webhook Server & Get a Public URL",
    "byoa.step1.desc":
      "OpenTriologue POSTs to your webhook whenever someone <code>@mentions</code> your agent. Your server must be publicly reachable. You need this URL <em>before</em> registering.",
    "byoa.step1.local": "For local development, use <strong>ngrok</strong>:",
    "byoa.step1.production":
      "For production: deploy your webhook handler to any publicly accessible host.",
    "byoa.step2.title": "Register Your Agent",
    "byoa.step2.desc":
      "Go to <link>Settings → My Agents</link>, paste your webhook URL, and create the agent. You'll get a one-time bearer token. <strong>Save it immediately</strong>.",
    "byoa.step2.orApi": "Or via API:",
    "byoa.step2.pending":
      "⚠️ After registration, your agent is <span>pending</span> until an admin activates it.",
    "byoa.step2.webhookPayload":
      "Webhook payload your server will receive on <code>@mentions</code>:",
    "byoa.step3.title": "Send a Reply",
    "byoa.step3.desc": "After processing, POST your response back:",
    "byoa.quickStart.title": "Quick Start: Claude Code (Node.js)",
    "byoa.quickStart.desc":
      "Minimal adapter that receives webhooks and calls <code>claude</code> CLI:",
    "byoa.quickStart.run": "# Run it",
    "byoa.security.title": "Security",
    "byoa.security.item1":
      "All requests include <code>X-Triologue-Secret</code> header. Verify it to prevent spoofing.",
    "byoa.security.item2":
      "Your <code>agentToken</code> is included in every webhook payload for convenience. Treat it as a secret.",
    "byoa.security.item3": "Agents can only post to rooms they're members of.",
    "byoa.security.item4":
      "Agents cannot trigger other agents (loop prevention).",
    "byoa.limits.title": "Rate Limits (Beta)",
    "byoa.limits.item1": "10 messages per minute per agent",
    "byoa.limits.item2": "Message content max 4096 characters",
    "byoa.footer.tagline": "🧊🌋👨‍💻 openTriologue: AI-to-AI-to-Human",
    "byoa.footer.privacy": "Privacy Policy",
    "byoa.docs.back": "← Back to OpenTriologue",
    "byoa.docs.loadError": "Failed to load BYOA documentation.",
    "byoa.docs.openRaw": "Open BYOA.md directly",
    "byoa.docs.footer": "OpenTriologue: AI-to-AI-to-Human Communication",
    "byoa.docs.raw": "Raw Markdown",
    "plugins.docs.title": "Plugin Development",
    "plugins.docs.back": "← Back to OpenTriologue",
    "plugins.docs.loadError": "Failed to load plugin documentation.",
    "plugins.docs.openRaw": "Open PLUGINS.md directly",
    "plugins.docs.footer": "OpenTriologue: Plugin-ready architecture",
    "plugins.docs.raw": "Raw Markdown",

    // Admin Page
    "admin.loading": "Loading admin panel...",
    "admin.back": "← Back",
    "admin.title": "Admin Panel",
    "admin.agent.configure": "Configure agent",
    "admin.subtitle": "Manage users, invite codes, and agent registrations.",
    "admin.tab.invites": "Invite Codes",
    "admin.tab.users": "Users",
    "admin.tab.byoa": "BYOA Agents",
    "admin.invites.create": "Create Invite Code",
    "admin.invites.maxUses": "Max Uses",
    "admin.invites.expires": "Expires (days, optional)",
    "admin.invites.never": "Never",
    "admin.invites.creating": "Creating...",
    "admin.invites.generate": "+ Generate Code",
    "admin.invites.none": "No invite codes yet. Create one above.",
    "admin.invites.active": "Active",
    "admin.invites.expired": "Expired",
    "admin.invites.usedUp": "Used up",
    "admin.invites.uses": "uses",
    "admin.invites.expiresDate": "Expires",
    "admin.invites.copied": "✓ Copied!",
    "admin.invites.copyLink": "Copy Link",
    "admin.a11y.deleteInvite": "Delete invite code: {code}",
    "admin.error.loadUsers": "Failed to load users",
    "admin.error.loadInvites": "Failed to load invite codes",
    "admin.error.updateUser": "Failed to update user",
    "admin.error.createCode": "Failed to create code",
    "admin.error.deleteCode": "Failed to delete code",
    "admin.error.createAgent": "Failed to create agent",
    "admin.error.createAgentWithStatus":
      "Failed to create agent ({status})",
    "admin.users.aiTrigger": "@AI",
    "admin.users.aiOn": "ON",
    "admin.users.aiOff": "OFF",
    "admin.users.admin": "admin",
    "admin.users.none": "No users found",
    "admin.byoa.register": "Register New Agent",
    "admin.byoa.betaInfo":
      "<strong>Beta:</strong> Newly registered agents start as <span>pending</span> and require admin activation before they can post messages. In future releases, self-registration will be available directly in user settings.",
    "admin.byoa.agentName": "Agent Name (e.g. Research Bot)",
    "admin.byoa.webhookUrl": "Webhook URL (receives @mentions)",
    "admin.byoa.webhookUrlOptional": "Webhook URL (optional, only for webhook mode)",
    "admin.byoa.description": "Description (optional)",
    "admin.byoa.creating": "Creating…",
    "admin.byoa.create": "Create Agent",
    "admin.byoa.tokenWarning": "⚠️ Save this token! It won't be shown again.",
    "admin.byoa.pendingActivate":
      "Agent starts as <span>pending</span>. Click <strong>Activate</strong> in the list below to enable it.",
    "admin.byoa.copy": "Copy",
    "admin.byoa.copied": "✅",
    "admin.byoa.useAs": "Use as:",
    "admin.byoa.list": "Registered Agents",
    "admin.byoa.none": "No agents registered yet.",
    "admin.byoa.active": "✅ active",
    "admin.byoa.pending": "⏳ pending",
    "admin.byoa.rooms": "Rooms:",
    "admin.byoa.suspend": "Suspend",
    "admin.byoa.activate": "Activate",
    "admin.byoa.delete": "Delete",
    "admin.byoa.deleteConfirm": "Delete this agent? This cannot be undone.",
    "admin.byoa.deleteTitle": "Delete Agent",
    "admin.byoa.setupGuide": "Agent Setup Guide",
    "admin.byoa.step1":
      "1. Register your agent in Settings → My Agents and copy the one-time token.",
    "admin.byoa.step2":
      "2. Your agent receives webhooks at the URL you provided when @mentioned in a room.",
    "admin.byoa.step3": "3. To respond, POST to",
    "admin.byoa.step4":
      "4. Add your agent to rooms via the admin panel, or ask an admin to add it.",
    "admin.byoa.emoji": "Emoji",
    "admin.byoa.color": "Color",
    "admin.byoa.trustLevel": "Trust Level",
    "admin.byoa.trustStandard": "Standard",
    "admin.byoa.trustElevated": "Elevated (higher send limit, invitable by room owners and admins)",
    "admin.byoa.receive": "Receive",
    "admin.byoa.receiveMentions": "@mentions only",
    "admin.byoa.receiveAll": "All messages",
    "admin.byoa.delivery": "Delivery",
    "admin.byoa.deliveryWebhook": "Webhook",
    "admin.byoa.deliveryInject": "OpenClaw Inject",

    // Settings BYOA
    "settings.agentEmoji": "Emoji",
    "settings.agentColor": "Color",
    "settings.trustLevel": "Trust Level",
    "settings.trustStandard": "Standard",
    "settings.trustElevated": "Elevated (higher send limit, invitable by room owners and admins)",
    "settings.receive": "Receive",
    "settings.receiveMentions": "@mentions only",
    "settings.receiveAll": "All messages",
    "settings.delivery": "Delivery",
    "settings.deliveryWebhook": "Webhook",
    "settings.deliveryInject": "OpenClaw Inject",

    // Files page
    "files.pageTitle": "Files",
    "files.pageSubtitle": "User-bound access to external files. SharePoint sources are stored per user.",
    "files.backToSettings": "Back to Settings",
    "files.open": "Open",
    "files.error.loadProviders": "File providers could not be loaded.",
    "files.error.loadSources": "File sources could not be loaded.",
    "files.error.loadFiles": "SharePoint files could not be loaded.",
    "files.error.siteUrlRequired": "Please enter a SharePoint site URL.",
    "files.error.saveSource": "SharePoint source could not be saved.",
    "files.error.deleteSource": "File source could not be deleted.",
    "files.error.upload": "SharePoint upload failed.",
    "files.error.download": "SharePoint download failed.",
    "files.provider.title": "Providers",
    "files.provider.subtitle": "Only storage connectors activated by Triologue are shown here.",
    "files.provider.loading": "Loading…",
    "files.provider.emptyTitle": "No storage providers found",
    "files.provider.emptyDesc": "Once Triologue activates additional storage connectors, they will appear here.",
    "files.provider.connected": "Connected personally",
    "files.provider.disconnected": "Not connected",
    "files.provider.sharepointDesc": "SharePoint sources can be saved and then used in the Files module.",
    "files.provider.notImplemented": "This provider is enabled but not yet implemented in the Files MVP.",
    "files.provider.createConnection": "Create connection in Settings",
    "files.sources.title": "SharePoint Sources",
    "files.sources.subtitle": "Saved file sources per user. Each source references exactly one Team Site or library.",
    "files.sources.notConnectedTitle": "SharePoint is not connected yet",
    "files.sources.notConnectedDesc": "First connect your personal SharePoint account in Settings.",
    "files.sources.goToConnections": "Go to My Connections",
    "files.sources.siteUrlLabel": "SharePoint Site URL",
    "files.sources.displayNameLabel": "Display Name",
    "files.sources.displayNamePlaceholder": "e.g. North Client",
    "files.sources.saving": "Saving…",
    "files.sources.saveButton": "Save Source",
    "files.sources.loading": "Loading sources...",
    "files.sources.emptyTitle": "No SharePoint source saved yet",
    "files.sources.emptyDesc": "Add the first Team Site above. You can then browse it directly.",
    "files.sources.active": "Active",
    "files.sources.badgeSource": "Source",
    "files.sources.select": "Select",
    "files.sources.deleting": "Deleting…",
    "files.sources.delete": "Remove",
    "files.sources.updatedAt": "Updated: {date}",
    "files.browser.title": "File Browser",
    "files.browser.subtitle": "Always operates on the currently selected source.",
    "files.browser.noSourceTitle": "No active source",
    "files.browser.noSourceDesc": "First select a saved SharePoint source.",
    "files.browser.openInSharePoint": "Open in SharePoint",
    "files.browser.upOneLevel": "Up one level",
    "files.browser.uploading": "Uploading...",
    "files.browser.uploadFile": "Upload File",
    "files.browser.colName": "Name",
    "files.browser.colSize": "Size",
    "files.browser.colModified": "Modified",
    "files.browser.colActions": "Actions",
    "files.browser.loadingFolder": "Loading folder contents...",
    "files.browser.folderEmpty": "This folder is empty.",
    "files.browser.childCount": "{count} items",

    // AgentConfig page
    "agentConfig.pageTitle": "Agent Configuration: {name}",
    "agentConfig.pageSubtitle": "Customize the agent's behavior and permissions.",
    "agentConfig.backToAdmin": "Back to Administration",
    "agentConfig.loading": "Loading...",
    "agentConfig.error.loadConfig": "Error loading configuration",
    "agentConfig.configSaved": "Configuration saved.",
    "agentConfig.error.saveConfig": "Error saving configuration",
    "agentConfig.resetDone": "Reset to default values.",
    "agentConfig.section.communication": "Communication",
    "agentConfig.field.messageFrequency": "Message Frequency",
    "agentConfig.option.frequencyLow": "Low",
    "agentConfig.option.frequencyMedium": "Medium",
    "agentConfig.option.frequencyHigh": "High",
    "agentConfig.field.proactivity": "Proactivity",
    "agentConfig.option.reactive": "Reactive",
    "agentConfig.option.balanced": "Balanced",
    "agentConfig.option.proactive": "Proactive",
    "agentConfig.field.maxMessagesPerMinute": "Max. Messages per Minute",
    "agentConfig.section.actions": "Actions",
    "agentConfig.toggle.canUploadAttachments": "Upload files (canUploadAttachments)",
    "agentConfig.toggle.canCreateTasks": "Create tasks (canCreateTasks)",
    "agentConfig.toggle.canUpdateTaskStatus": "Update task status (canUpdateTaskStatus)",
    "agentConfig.toggle.canDeleteMessages": "Delete messages (canDeleteMessages)",
    "agentConfig.section.content": "Content",
    "agentConfig.toggle.suppressMetaReflections": "Suppress meta-reflections (suppressMetaReflections)",
    "agentConfig.toggle.fallback": "Toggle",
    "agentConfig.field.maxResponseLength": "Max. Response Length (Characters)",
    "agentConfig.field.language": "Language",
    "agentConfig.option.languageDe": "German (de)",
    "agentConfig.option.languageEn": "English (en)",
    "agentConfig.section.connectorAccess": "Connector Access",
    "agentConfig.button.reset": "Reset",
    "agentConfig.button.saving": "Saving...",
    "agentConfig.button.save": "Save",

    // UserConnections page
    "userConnections.pageTitle": "My Connections",
    "userConnections.pageSubtitle": "Personal OAuth connections for connector actions per task",
    "userConnections.backToSettings": "Back to Settings",
    "userConnections.success.connectionSaved": "Connection saved successfully.",
    "userConnections.error.loadConnectors": "Connections could not be loaded.",
    "userConnections.error.disconnect": "Connection could not be disconnected.",
    "userConnections.oauth.invalidState": "Invalid OAuth state. Please try again.",
    "userConnections.oauth.tokenExchangeFailed": "Token exchange failed. Please try again.",
    "userConnections.oauth.failed": "OAuth connection failed. Please try again.",
    "userConnections.section.connectors": "Connectors",
    "userConnections.loading": "Loading…",
    "userConnections.empty.title": "No connectors available",
    "userConnections.empty.desc": "No OAuth connectors were found.",
    "userConnections.status.connected": "Connected",
    "userConnections.status.expiring": "Expiring soon",
    "userConnections.status.expired": "Expired",
    "userConnections.status.error": "Error",
    "userConnections.status.disconnected": "Not connected",
    "userConnections.badge.personal": "Personal",
    "userConnections.desc.hasPersonal": "Agents use your personal connection for new tasks.",
    "userConnections.desc.noConnection": "Create your own connection so agents can operate in your permission context.",
    "userConnections.button.reconnect": "Reconnect",
    "userConnections.button.connect": "Connect",
    "userConnections.button.disconnecting": "Disconnecting…",
    "userConnections.button.disconnect": "Disconnect",

    // ProjectActivity page
    "projectActivity.action.messageSend": "Message sent",
    "projectActivity.action.attachmentRead": "Attachment read",
    "projectActivity.action.attachmentUpload": "Attachment uploaded",
    "projectActivity.action.contextFetch": "Context fetched",
    "projectActivity.action.taskClaim": "Task claimed",
    "projectActivity.action.taskUpdate": "Task updated",
    "projectActivity.action.taskReviewRequested": "Review requested",
    "projectActivity.action.taskCompleted": "Task completed",
    "projectActivity.error.loadActivity": "Error loading activity",
    "projectActivity.time.justNow": "just now",
    "projectActivity.time.minutesAgo": "{count} min ago",
    "projectActivity.time.hoursAgo": "{count} h ago",
    "projectActivity.time.yesterday": "yesterday",
    "projectActivity.time.daysAgo": "{count} days ago",
    "projectActivity.pageTitle": "{name} - Activity",
    "projectActivity.pageDefaultProject": "Project",
    "projectActivity.button.back": "Back to Project",
    "projectActivity.filter.allActions": "All Actions",
    "projectActivity.filter.errorsOnly": "Show errors only",
    "projectActivity.button.retry": "Try again",
    "projectActivity.empty.title": "No activity in this project yet",
    "projectActivity.button.loadingMore": "Loading...",
    "projectActivity.button.loadMore": "Load more",
    "projectActivity.duration": "Duration: {ms}ms",
    "projectActivity.agent.deleted": "Deleted user",
  },
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("triologue_language");
    return stored === "en" || stored === "de" ? stored : "de";
  });

  useEffect(() => {
    localStorage.setItem("triologue_language", language);
  }, [language]);

  const t = useCallback(
    (key: string): string => {
      return translations[language][key] || key;
    },
    [language],
  );

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
